import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useParams } from "react-router";

import { Chart } from "chart.js/auto";

import { ApiCallResult, LogoutHook, useAPI } from "services/Api";
import AssessmentDeterminations from "reports/AssessmentDeterminations";
import CustomDropDown from "input/CustomDropDown";
import ManagedInput from "input/ManagedInput";
import ManagedTextArea from "input/ManagedTextArea";
import Page from "pages/Page";
import ReportDiscoveriesManager from "reports/ReportDiscoveriesManager";
import ReportGoalsManager from "reports/ReportGoalsManager";
import ReportSectionDivider from "reports/ReportSectionDivider";
import CitationDisplay from "reports/CitationDisplay";
import { AutoSaveReportPatcher, AutoSaveStatus } from "reports/AutoSaveReportPatcher";
import { CindraSubjectiveProgressSummary } from "cindra/CindraSubjectiveProgressSummary";
import { useCindraEnabled } from "cindra/CindraUtils";
import { DebugLevel, UpdateMode, useDirtyState, useDirtyStateManager, useDirtyStateO } from "utility/DirtyStateManager";
import { ModularHeaderButton, ModularHeaderConfig } from "pages/ModularHeader";
import { colorizeJson } from "utility/Utils";
import { customReportColors } from "reports/Report";
import { generateAssessments as generateAssessmentsUtil, AssessmentDecision } from "reports/AssessmentGenerator";
import { navigate } from "utility/NavigationService";
import { renderKeyImprovedTrajectoriesGraph } from "reports/KeyImprovedTrajectoriesGraph";
import { renderLoader } from "utility/LoaderUtils";
import { useNavigationBlocker } from "utility/NavigationBlocker";
import { useNotificationManager } from "notifications/NotificationManager";

import * as MD from "reports/measures/MeasureDef";
import * as R from "reports/Report";
import * as RD from "reports/ReportDef";
import * as RDP from "reports/ReportDataProcessor";
import * as RP from "reports/ReportPdf";
import * as T from "reports/measures/Trajectory";

type ReportProcessorRouteParams = Record<string, string>;

const ReportProcessor = ({ componentName="[ReportProcessor]" }) => {
    const { generate: generateParam, id: patientIdParam, reportId: reportIdParam } = useParams<ReportProcessorRouteParams>();
    const isGenerator: boolean = generateParam ? JSON.parse(generateParam.toLowerCase()) : false;
    const reportId: number = reportIdParam ? parseInt(reportIdParam) : undefined;

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const cindraEnabled = useCindraEnabled();

    // ---=== React Default useState ===--- //

    //#region [useState] Page & Report Layout Configuration
    const [validationEnabled, setValidationEnabled] = useState<boolean>(false);
    const [headerConfig, setHeaderConfig] = useState<ModularHeaderConfig>(null);
    const [toggleObjectiveOutcomesSection, setToggleObjectiveOutcomesSection] = useState<boolean>(false);
    const [showAssessmentDeterminationsModal, setShowAssessmentDeterminationsModal] = useState<boolean>(false);
    const [assessmentDecisions, setAssessmentDecisions] = useState<AssessmentDecision[]>([]);
    //#endregion [useState] Page Configuration

    //#region [useState] Report Configuration
    const [preparedDate, setPreparedDate] = useState<Date>(null);
    const [preparerName, setPreparerName] = useState<string>(null);
    const [report, setReport] = useState<RD.EditableReport>(null);
    const [reportWeekOptions, setReportWeekOptions] = useState<RD.ReportWeekOptions>([]);
    const [samplePlanWeeks, setSamplePlanWeeks] = useState<number>(0);
    //#endregion [useState] Report Configuration

    //#region [useState] Patient Info
    const [claimNumber, setClaimNumber] = useState<string>("");
    const [coach, setCoach] = useState<string>(null);
    const [dateOfBirth, setDateOfBirth] = useState<string>("");
    const [dateOfInjury, setDateOfInjury] = useState<string>("");
    const [hasPhysicianOversight, setHasPhysicianOversight] = useState<boolean>(false);
    const [patientId, setPatientId] = useState<number>(patientIdParam ? parseInt(patientIdParam) : undefined);
    const [patientName, setPatientName] = useState<string>(null);
    const [patientNameMasked, setPatientNameMasked] = useState<string>(null);
    const [payerType, setPayerType] = useState<RD.PayerEnums>(RD.PayerEnums.KarunaHome);
    const [physician, setPhysician] = useState<string>("");
    //#endregion [useState] Patient Info

    //#region [useState] Program Info
    const [brand, setBrand] = useState<RD.BrandEnums>(RD.BrandEnums.KarunaHome);
    const [programType, setProgramType] = useState<string>("");
    //#endregion [useState] Program Info

    //#region [useState] Citations
    const [citations, setCitations] = useState<RD.Citations>([]);
    const [historyOfInjuryCitation, setHistoryOfInjuryCitation] = useState<number | undefined>(undefined);
    const [subjectiveProgressSummaryCitation, setSubjectiveProgressSummaryCitation] = useState<number | undefined>(undefined);
    //#endregion [useState] Citations

    //#region [useState] Verifier Reversion Operations
    const [discoveriesOriginal, setDiscoveriesOriginal] = useState<RD.Discoveries>([]);
    const [goalsOriginal, setGoalsOriginal] = useState<RD.Goals>([]);
    const [historyOfInjuryOriginal, setHistoryOfInjuryOriginal] = useState<string>("");
    const [planOriginal, setPlanOriginal] = useState<string>("");
    const [socialAssessmentsOriginal, setSocialAssessmentsOriginal] = useState<string>("");
    const [subjectiveProgressSummaryOriginal, setSubjectiveProgressSummaryOriginal] = useState<string>("");
    //#endregion [useState] Verifier Reversion Operations

    //#region [useState] Report Operations
    const [autoSaveGeneratedData, setAutoSaveGeneratedData] = useState<RD.ReportGeneratedData>(null);
    const [isReportConfigurationDirty, setIsReportConfigurationDirty] = useState<boolean>(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>(AutoSaveStatus.Idle);
    const [hasCoachingPlan, setHasCoachingPlan] = useState<boolean>(true);
    const [isGeneratingSubjectiveSummary, setIsGeneratingSubjectiveSummary] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadingMessage, setLoadingMessage] = useState<string>(`${isGenerator && !reportId ? "Creating" : "Retrieving"} Outcome Report`);
    const [reportPostProcessingReplaceAutoSavedData, setReportPostProcessingReplaceAutoSavedData] = useState<boolean>(false);
    const [reportPostProcessingTrigger, setReportPostProcessingTrigger] = useState<number>(0);
    const [triggerApproveReport, setTriggerApproveReport] = useState<number>(0);
    const [triggerDeleteReportWarning, setTriggerDeleteReportWarning] = useState<number>(0);
    const [triggerPreviewPdf, setTriggerPreviewPdf] = useState<number>(0);
    const [triggerSubmitForReview, setTriggerSubmitForReview] = useState<number>(0);
    //#endregion [useState] Report Operations

    // ---=== DirtyStateManager: Tracked useDirtyState ===--- //

    const dirtyManager = useDirtyStateManager();

    const DirtyCategory = {
        REPORT_CONFIGURATION: "Report Configuration",
        KEY_IMPROVEMENTS: "Key Improvements",
        HISTORY_OF_INJURY: "History Of Injury",
        SUBJECTIVE_OUTCOMES: "Subjective Outcomes",
        OBJECTIVE_OUTCOMES: "Objective Outcomes",
        ASSESSMENTS: "Assessments",
        PLAN: "Plan"
    };

    const [isDirtyStateManagerInitialized, setIsDirtyStateManagerInitialized] = useState<boolean>(false);

    //#region [useState] Report Configuration
    const [reportPlanWeeks, setReportPlanWeeks] = useDirtyState<number>(0, DirtyCategory.REPORT_CONFIGURATION, "reportPlanWeeks");
    const [reportWeek, setReportWeek] = useDirtyState<RD.ReportWeek>(0, DirtyCategory.REPORT_CONFIGURATION, "reportWeek");
    //#endregion [useDirtyState] Report Configuration

    //#endregion [useDirtyState] Key Improvements
    const [selectedImprovedTrajectoryTypes, setSelectedImprovedTrajectoryTypes] = useDirtyState<MD.MeasureTypes>([], DirtyCategory.KEY_IMPROVEMENTS, "selectedImprovedTrajectoryTypes");
    //#endregion [useDirtyState] Key Improvements

    //#region [useDirtyState] History of Injury
    const historyOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: historyOfInjury, set: setHistoryOfInjury, update: updateHistoryOfInjury }
        = useDirtyStateO<string>("", DirtyCategory.HISTORY_OF_INJURY, "historyOfInjury", historyOptions);
    // //#endregion [useDirtyState] History of Injury

    //#region [useDirtyState] Subjective Outcomes
    const discoveriesOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: discoveries, set: setDiscoveries, update: updateDiscoveries }
        = useDirtyStateO<RD.Discoveries>([], DirtyCategory.SUBJECTIVE_OUTCOMES, "discoveries", discoveriesOptions);
    const [engagementLevel, setEngagementLevel] = useDirtyState<RD.EngagementLevel | null>(null, DirtyCategory.SUBJECTIVE_OUTCOMES, "engagementLevel");
    const goalsOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: goals, set: setGoals, update: updateGoals }
        = useDirtyStateO<RD.Goals>([], DirtyCategory.SUBJECTIVE_OUTCOMES, "goals", goalsOptions);
    const [goalsProgress, setGoalsProgress] = useDirtyState<RD.GoalsProgress | null>(null, DirtyCategory.SUBJECTIVE_OUTCOMES, "goalsProgress");
    const subjectiveProgressSummaryOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: subjectiveProgressSummary, set: setSubjectiveProgressSummary, update: updateSubjectiveProgressSummary }
        = useDirtyStateO<RD.SubjectiveProgressSummary>("", DirtyCategory.SUBJECTIVE_OUTCOMES, "subjectiveProgressSummary", subjectiveProgressSummaryOptions);
    // Inclusions
    const [includeEngagementLevel, setIncludeEngagementLevel] = useDirtyState<boolean>(false, DirtyCategory.SUBJECTIVE_OUTCOMES, "includeEngagementLevel");
    const [includeGoalsProgress, setIncludeGoalsProgress] = useDirtyState<boolean>(false, DirtyCategory.SUBJECTIVE_OUTCOMES, "includeGoalsProgress");
    //#endregion [useDirtyState] Subjective Outcomes

    //#region [useDirtyState] Objective Outcomes
    const [allTrajectories, setAllTrajectories] = useState<T.Trajectories>([]);
    const [averageActivityTimePerWeek, setAverageActivityTimePerWeek] = useDirtyState<string>(null, DirtyCategory.OBJECTIVE_OUTCOMES, "averageActivityTimePerWeek");
    const [medicationReductionStatus, setMedicationReductionStatus] = useDirtyState<RD.MedicationReductionStatus>(null, DirtyCategory.OBJECTIVE_OUTCOMES, "medicationReductionStatus");
    const [returnToWorkStatus, setReturnToWorkStatus] = useDirtyState<RD.ReturnToWorkStatus>(null, DirtyCategory.OBJECTIVE_OUTCOMES, "returnToWorkStatus");
    const [sessionsAttended, setSessionsAttended] = useDirtyState<number>(null, DirtyCategory.OBJECTIVE_OUTCOMES, "sessionsAttended");
    const [uniqueDays, setUniqueDays] = useDirtyState<number>(null, DirtyCategory.OBJECTIVE_OUTCOMES, "uniqueDays");
    // Inclusions
    const [includeAverageTime, setIncludeAverageTime] = useDirtyState<boolean>(false, DirtyCategory.OBJECTIVE_OUTCOMES, "includeAverageTime");
    const [includeMedicationReduction, setIncludeMedicationReduction] = useDirtyState<boolean>(false, DirtyCategory.OBJECTIVE_OUTCOMES, "includeMedicationReduction");
    const [includeReturnToWork, setIncludeReturnToWork] = useDirtyState<boolean>(false, DirtyCategory.OBJECTIVE_OUTCOMES, "includeReturnToWork");
    const [includeSessionsAttended, setIncludeSessionsAttended] = useDirtyState<boolean>(false, DirtyCategory.OBJECTIVE_OUTCOMES, "includeSessionsAttended");
    const [includeUniqueDays, setIncludeUniqueDays] = useDirtyState<boolean>(false, DirtyCategory.OBJECTIVE_OUTCOMES, "includeUniqueDays");
    const [includedTrajectories, setIncludedTrajectories]
        = useDirtyState<Record<MD.MeasureType, boolean>>(
            Object.fromEntries(
                Object.keys(MD.MeasureTypesMap).map(key => [key, false])
            ) as Record<MD.MeasureType, boolean>,
            DirtyCategory.OBJECTIVE_OUTCOMES,
            "includedTrajectories"
        );
    //#endregion [useDirtyState] Objective Outcomes

    //#region [useDirtyState] Assessments
    const [assessmentsGenerated, setAssessmentsGenerated] = useDirtyState<boolean>(!isGenerator, DirtyCategory.ASSESSMENTS, "assessmentsGenerated");
    const behavioralAssessmentsOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: behavioralAssessments, set: setBehavioralAssessments, update: updateBehavioralAssessments }
        = useDirtyStateO<string>("", DirtyCategory.ASSESSMENTS, "behavioralAssessments", behavioralAssessmentsOptions);
    const continuedNeedsAssessmentsOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: continuedNeedsAssessments, set: setContinuedNeedsAssessments, update: updateContinuedNeedsAssessments }
        = useDirtyStateO<string>("", DirtyCategory.ASSESSMENTS, "continuedNeedsAssessments", continuedNeedsAssessmentsOptions);
    const physicalAssessmentsOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: physicalAssessments, set: setPhysicalAssessments, update: updatePhysicalAssessments }
        = useDirtyStateO<string>("", DirtyCategory.ASSESSMENTS, "physicalAssessments", physicalAssessmentsOptions);
    const socialAssessmentsOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: socialAssessments, set: setSocialAssessments, update: updateSocialAssessments }
        = useDirtyStateO<string>("", DirtyCategory.ASSESSMENTS, "socialAssessments", socialAssessmentsOptions);
    // Inclusions
    const [includeBehavioralAssessments, setIncludeBehavioralAssessments] = useDirtyState<boolean>(!isGenerator, DirtyCategory.ASSESSMENTS, "includeBehavioralAssessments");
    const [includeContinuedNeedsAssessments, setIncludeContinuedNeedsAssessments] = useDirtyState<boolean>(!isGenerator, DirtyCategory.ASSESSMENTS, "includeContinuedNeedsAssessments");
    const [includePhysicalAssessments, setIncludePhysicalAssessments] = useDirtyState<boolean>(!isGenerator, DirtyCategory.ASSESSMENTS, "includePhysicalAssessments");
    const [includeSocialAssessments, setIncludeSocialAssessments] = useDirtyState<boolean>(!isGenerator, DirtyCategory.ASSESSMENTS, "includeSocialAssessments");
    //#endregion [useDirtyState] Assessments

    //#region [useDirtyState] Plan
    const planOptions = useMemo(() => ({ updateMode: "debounced" as UpdateMode, debounceMs: 300 }), []);
    const { value: plan, set: setPlan, update: updatePlan }
        = useDirtyStateO<string>("", DirtyCategory.PLAN, "plan", planOptions);
    const [planSettings, setPlanSettings]
        = useDirtyState<RD.ReportGeneratedPlanSettings>(
            {
                extensionWeeks: 1,
                physicianNotification: false,
                physicianNotificationType: RD.PhysicianNotificationType.PsychotherapyReferral,
                recommendProgramContinuation: false,
                recommendProgramEnd: false,
                recommendProgramExtension: false
            } as RD.ReportGeneratedPlanSettings,
            DirtyCategory.PLAN,
            "planSettings"
        );
    //#endregion [useDirtyState] Plan

    // ---=== Global const vars ===--- //

    const assessmentsDividerRef = useRef<HTMLDivElement>(null);
    const chartRef: React.RefObject<Chart<"line", number[]>> = useRef<Chart<"line", number[]>>(null);

    // ---=== Utilities ===--- //

    const goToPatient = () => {
        void navigate(`/patient/${patientId}/reports`);
    };

    const goToHome = () => {
        void navigate("/");
    };

    const createHeaderConfig = useCallback(() => {
        if (!report || !patientName) {
            return;
        }

        let headerConfigData = {
            trail: breadCrumbLinks(isGenerator),
            body: {
                subheader: isGenerator
                    ? "Create Outcome Report"
                    : `Review ${R.isMonth6Report(reportWeek) ? "6 Month Report" : `Week ${Number(reportWeek)}`} Outcome Report`,
                name: patientName,
                buttons: [
                    {
                        title: "Back",
                        img: "/icons/flip-backward.svg",
                        click: isGenerator ? goToPatient : goToHome,
                        disabled: false
                    }
                ] as ModularHeaderButton[]
            }
        };

        if (!isGenerator) {
            headerConfigData.body.buttons = [
                ...headerConfigData.body.buttons,
                {
                    title: "Preview Final PDF",
                    img: "/icons/file-download-02-black.svg",
                    click: () => !notificationManager.hasErrors() && setTriggerPreviewPdf(prev => ++prev),
                    disabled: notificationManager.hasErrors()
                }
            ];
        }

        let autoSaveImagePath: string = "/icons/autosave-idle.svg";
        let autoSaveStatusText: string = "Saved"
        let autoSaveStatusTextColor: string = "text-autosave-blue"
        let autoSaveStatusOpacity: string = "opacity-100";
        switch (autoSaveStatus) {
            case AutoSaveStatus.Idle:
                break;
            case AutoSaveStatus.Pending:
                autoSaveImagePath = "/icons/autosave-pending.svg";
                autoSaveStatusText = "Modified";
                autoSaveStatusTextColor = "text-autosave-orange"
                autoSaveStatusOpacity = "opacity-55";
                break;
            case AutoSaveStatus.InProgress:
                autoSaveImagePath = "/icons/autosave-inprogress.svg";
                autoSaveStatusText = "Saving...";
                autoSaveStatusTextColor = "text-autosave-orange";
                break;
        }

        headerConfigData.body.buttons = [
            ...headerConfigData.body.buttons,
            {
                title: autoSaveStatusText,
                img: autoSaveImagePath,
                disabled: true,
                useDefaultClasses: false,
                customButtonBaseClasses: `inline-flex justify-center items-center gap-1 h-[80%] max-[1200px]:h-[70%] max-[1200px]:bottom-[15%] px-0 py-2 ${autoSaveStatusOpacity}`,
                customDisabledTextClasses: `pt-0.5 h-[20px] ${autoSaveStatusTextColor}`
            }
        ];

        setHeaderConfig(headerConfigData);
    }, [autoSaveStatus, patientName, report, reportWeek]);

    // ---=== History of Injury ===--- //

    const dispatchRevertHistoryOfInjury = () => {
        if (historyOfInjuryOriginal?.length > 0) {
            updateHistoryOfInjury(historyOfInjuryOriginal);
        }
    };

    // ---=== Subjective Outcomes ===--- //

    const dispatchRevertSubjectiveProgressSummary = () => {
        if (subjectiveProgressSummaryOriginal) {
            updateSubjectiveProgressSummary(`${subjectiveProgressSummaryOriginal}\n\n`);
        }
    };

    // ---=== Objective Outcomes ===--- //

    const isTrajectoryIncluded = (measureType: MD.MeasureType) => {
        return includedTrajectories[measureType] ?? false;
    };

    const toggleIncludedTrajectoryCallback = useCallback((measureType: MD.MeasureType, include: boolean) => {
        setIncludedTrajectories(prev => ({ ...prev, [measureType]: include }));
    }, [setIncludedTrajectories]);

    const toggleKeyImprovedTrajectoryCallback = useCallback((measureType: MD.MeasureType, include: boolean) => {
        if (include) {
            if (!selectedImprovedTrajectoryTypes) {
                setSelectedImprovedTrajectoryTypes([measureType]);
            }
            else {
                if (selectedImprovedTrajectoryTypes?.length < 5) {
                    setSelectedImprovedTrajectoryTypes(
                        (prev: MD.MeasureTypes) => {
                            if (!prev.includes(measureType)) {
                                return [...prev, measureType];
                            }
                            return prev;
                        }
                    );
                }
            }
        }
        else {
            setSelectedImprovedTrajectoryTypes(
                (prev: MD.MeasureTypes) => {
                    return prev.filter(type => type !== measureType)
                }
            );
        }
    }, [selectedImprovedTrajectoryTypes, setSelectedImprovedTrajectoryTypes]);

    // ---=== Assessments ===--- //

    const generateAssessments = () => {
        if (assessmentsGenerated) {
            return;
        }

        // Use the shared assessment generation utility
        const results = generateAssessmentsUtil(allTrajectories, includedTrajectories);

        // Store decisions for the Assessment Determinations modal
        setAssessmentDecisions(results.decisions);

        let physicalAssessmentsText = results.physical;
        let behavioralAssessmentsText = results.behavioral;
        let socialAssessmentsText = results.social;
        let continuedNeedsAssessmentsText = results.continuedNeeds;

        // For VA patients, always redact patient names in auto-generated assessments
        if (payerType === RD.PayerEnums.VA) {
            physicalAssessmentsText = R.redactPatientName(physicalAssessmentsText, patientName, patientNameMasked);
            behavioralAssessmentsText = R.redactPatientName(behavioralAssessmentsText, patientName, patientNameMasked);
            socialAssessmentsText = R.redactPatientName(socialAssessmentsText, patientName, patientNameMasked);
            continuedNeedsAssessmentsText = R.redactPatientName(continuedNeedsAssessmentsText, patientName, patientNameMasked);
        }

        // Physical Assessments
        if (physicalAssessmentsText.length > 0) {
            physicalAssessmentsText = physicalAssessmentsText.trimEnd();
        }
        setPhysicalAssessments(physicalAssessmentsText);
        setIncludePhysicalAssessments(physicalAssessmentsText.length > 0);

        // Behavioral Assessments
        if (behavioralAssessmentsText.length > 0) {
            behavioralAssessmentsText = behavioralAssessmentsText.trimEnd();
        }
        setBehavioralAssessments(behavioralAssessmentsText);
        setIncludeBehavioralAssessments(behavioralAssessmentsText.length > 0);

        // Social Assessments
        if (socialAssessmentsText.length > 0) {
            socialAssessmentsText = socialAssessmentsText.trimEnd();
        }
        setSocialAssessments(socialAssessmentsOriginal + "\n\n" + socialAssessmentsText);
        setIncludeSocialAssessments(socialAssessments.length > 0);

        // Continued Need Assessments
        if (continuedNeedsAssessmentsText.length > 0) {
            continuedNeedsAssessmentsText = continuedNeedsAssessmentsText.trimEnd();
        }
        setContinuedNeedsAssessments(continuedNeedsAssessmentsText);
        setIncludeContinuedNeedsAssessments(continuedNeedsAssessmentsText.length > 0);

        // Generated Assessments Markers
        setToggleObjectiveOutcomesSection(prev => !prev);
        setAssessmentsGenerated(true);
    };

    const confirmAssessmentsReset = () => {
        let text
            = `WARNING!!\n\n`
            + `Are you sure you want to edit the measures included in the report?\n\n`
            + `This will require the Assessments section to be regenerated which will `
            + `clear any changes you have made.`;
        if (window.confirm(text) === true) {
            revertAssessments();
        }
    };

    const dispatchRevertSocialAssessments = () => {
        if (socialAssessmentsOriginal?.length > 0) {
            updateSocialAssessments(socialAssessmentsOriginal);
        }
    };

    const revertAssessments = () => {
        setAssessmentsGenerated(false);
        setBehavioralAssessments("");
        setContinuedNeedsAssessments("");
        setPhysicalAssessments("");
        // setSocialAssessments(socialAssessmentsOriginal ?? "");
        setSocialAssessments("");
        setIncludeBehavioralAssessments(false);
        setIncludeContinuedNeedsAssessments(false);
        setIncludePhysicalAssessments(false);
        setIncludeSocialAssessments(false);

        // updateAssessmentsGeneratedNotification(false);
    };

    // ---=== Plan ===--- //

    const generatePlan = (
        settings: RD.ReportGeneratedPlanSettings
    ) => {
        // For VA patients, use full masked name (e.g., "Patient 460")
        // For non-VA patients, use just first name (e.g., "John")
        let patientFirstName: string = payerType === RD.PayerEnums.VA
            ? patientNameMasked
            : patientNameMasked?.split(" ")[0];
        let programName: string;
        let planText: string = "";

        switch (brand) {
            case RD.BrandEnums.Reclaim:
                programName = "Reclaim";
                break;
            case RD.BrandEnums.Valor:
                programName = "Valor";
                break;
            case RD.BrandEnums.KarunaHome:
            default:
                programName = "KarunaHOME";
                break;
        }

        if (settings.recommendProgramContinuation) {
            let aspirationsText: string = "";
            if (!R.isMonth6Report(reportWeek) && Number(reportWeek) < 12) {
                let aspirations: string[] = [
                    "Pain Defined", "Neural Circuit Pain and Calming the Nervous System", "Danger Signals", "Graded Exposure", "Emotions", "Thoughts and Behaviors", "Stress Interpretation and Relaxation Skills", "Mindfulness and Gratitude", "Enjoyable Activities", "Social Connection", "Restoring Life Balance", "Reclaiming Your Life", "Planning For Success"
                ]
                aspirationsText = `${aspirations.slice(Number(reportWeek) - 1, 11).join(", ")}`;
            }
            planText
                += `Given ${patientFirstName}'s progress and current needs, it is my recommendation `
                + `to continue with the ongoing ${programName} program.\n\n`
                + `${aspirationsText.length > 0
                    ? `The upcoming weeks will cover the following topics, tailored to support ${patientFirstName}'s `
                    + `specific goals and to build upon their existing knowledge:\n\n`
                    + `${aspirationsText}\n\n`
                    : ""
                }`
                + `${patientFirstName} will be encouraged to continue engaging with 5-25 minutes of `
                + `daily VET™ and maintain regular physical activity. Regular check-ins and adjustments `
                + `will be made as necessary to ensure the patient's optimal engagement and benefit.`;
        }

        if (settings.physicianNotification) {
            let notificationText: string;
            switch (settings.physicianNotificationType) {
                case RD.PhysicianNotificationType.PsychotherapyReferral:
                    notificationText
                        = `has expressed challenges with their mental health. `
                        + `They are interested in exploring options such as psychotherapy or counseling.`;
                    break;
                case RD.PhysicianNotificationType.MedicationReEvaluation:
                    notificationText
                        = `has voiced concerns regarding their current medication regimen. `
                        + `They would like to request a re-evaluation of their current needs `
                        + `to address any potential side-effects or concerns.`;
                    break;
                case RD.PhysicianNotificationType.NewInjury:
                    notificationText
                        = `has reported a recent injury and is requesting further medical attention `
                        + `to assess the extent of the injury and advise any necessary modifications `
                        + `to their current plan of care.`;
                    break;
                case RD.PhysicianNotificationType.NewSymptoms:
                    notificationText
                        = `has reported experiencing new concerning symptoms. `
                        + `They are requesting a physician re-evaluation to ascertain the cause and `
                        + `implications of these symptoms.`;
                    break;
            }

            planText
                += `${planText.length > 0 ? "\n\n" : ""}`
                + `${patientFirstName} `
                + `${notificationText}`;
        }

        if (settings.recommendProgramExtension) {
            let template
                = `${planText.length > 0 ? "\n\n" : ""}`
                + `In consideration of the subjective and objective progress exhibited by ${patientFirstName} `
                + `over the initial ${reportWeek} week${Number(reportWeek) > 1 ? "s" : ""} of the ${programName} program, `
                + `it is my recommendation that a request for an additional ${settings.extensionWeeks} `
                + `week${settings.extensionWeeks > 1 ? "s" : ""} be submitted for authorization.`;

            planText += template;
        }

        if (settings.recommendProgramEnd) {
            let template
                = `Given ${patientFirstName}'s progress and current needs, I recommend that they `
                + `continue implementing the skills and concepts of the ${programName} program `
                + `independently. Having completed the core 12-week curriculum, ${patientFirstName} `
                + `will be encouraged to continue working towards their goals.`
                + (R.isMonth6Report(reportWeek) ? '' : ` ${patientFirstName} will have a 6-month check-in, and adjustments will be made as necessary to ensure their optimal engagement and benefit. `)
                + `If ${patientFirstName}'s current state starts to decline, it is my recommendation `
                + `that they are re-evaluated for additional sessions with the ${programName} program.`;
            planText += ((planText.length > 0 ? "\n\n" : "") + template);
        }

        updatePlan(planText);

        // updatePlanSelectionNotification(planText);
    };

    const dispatchUpdateExtensionWeeks = (additionalWeeks: number) => {
        if (additionalWeeks !== planSettings.extensionWeeks) {
            const updatedSettings: RD.ReportGeneratedPlanSettings = {
                ...planSettings,
                extensionWeeks: additionalWeeks
            };
            setPlanSettings(updatedSettings);
            generatePlan(updatedSettings);
        }
    };

    const dispatchUpdatePhysicianNotification = (notify: boolean) => {
        const updatedSettings: RD.ReportGeneratedPlanSettings = {
            ...planSettings,
            physicianNotification: notify
        };
        setPlanSettings(updatedSettings);
        generatePlan(updatedSettings);
    };

    const dispatchUpdatePhysicianNotificationType = (type: RD.PhysicianNotificationType) => {
        if (type !== planSettings.physicianNotificationType) {
            const updatedSettings: RD.ReportGeneratedPlanSettings = {
                ...planSettings,
                physicianNotificationType: type
            };
            setPlanSettings(updatedSettings);
            generatePlan(updatedSettings);
        }
    };

    const dispatchUpdateRecommendProgramContinuation = (recommend: boolean) => {
        const updatedSettings: RD.ReportGeneratedPlanSettings = {
            ...planSettings,
            recommendProgramContinuation: recommend
        };
        setPlanSettings(updatedSettings);
        generatePlan(updatedSettings);
    };

    const dispatchUpdateRecommendProgramEnd = (recommend: boolean) => {
        const updatedSettings: RD.ReportGeneratedPlanSettings = {
            ...planSettings,
            recommendProgramEnd: recommend
        };
        setPlanSettings(updatedSettings);
        generatePlan(updatedSettings);
    };

    const dispatchUpdateRecommendProgramExtension = (recommend: boolean) => {
        const updatedSettings: RD.ReportGeneratedPlanSettings = {
            ...planSettings,
            recommendProgramExtension: recommend
        };
        setPlanSettings(updatedSettings);
        generatePlan(updatedSettings);
    };

    const dispatchRevertPlan = () => {
        dirtyManager.revertToOriginal(DirtyCategory.PLAN);
        if (planOriginal?.length > 0) {
            setPlan(planOriginal);
        }
    };

    // ---=== Report Data Processing ===--- //

    const validateReport = (validatePatientNameMask: boolean): boolean => {

        notificationManager.clearAllNotifications();

        // For VA patients, optionally apply redactions to user-entered content before validation
        // Auto-generated content (assessments, plan) is already redacted at generation time
        // This behavior is controlled by VITE_OUTCOME_REPORTS_AUTO_REDACT env var
        const shouldRedactUserContent = payerType === RD.PayerEnums.VA && RD.isOutcomeReportsAutoRedactEnabled();
        console.log('[ReportProcessor][validateReport] Patient info:', {
            payerType,
            isVA: payerType === RD.PayerEnums.VA,
            autoRedactEnabled: RD.isOutcomeReportsAutoRedactEnabled(),
            patientName,
            patientNameMasked,
            shouldRedactUserContent
        });

        // Log continuedNeedsAssessments before redaction for debugging
        if (shouldRedactUserContent) {
            console.log('[validateReport] BEFORE redaction - continuedNeedsAssessments:', {
                length: continuedNeedsAssessments?.length,
                preview: continuedNeedsAssessments?.substring(0, 200),
                containsName: continuedNeedsAssessments?.includes(patientName),
                containsFirstName: continuedNeedsAssessments?.includes(patientName.split(" ")[0])
            });
        }

        const contentToValidate = shouldRedactUserContent ? {
            // Auto-generated content - passed as-is (already redacted at generation time)
            behavioralAssessments,
            continuedNeedsAssessments,
            physicalAssessments,
            plan,
            socialAssessments,
            // User-entered content - redact if auto-redact is enabled
            discoveries: R.redactDiscoveriesPatientName(discoveries, patientName, patientNameMasked),
            goals: R.redactGoalsPatientName(goals, patientName, patientNameMasked),
            historyOfInjury: R.redactPatientName(historyOfInjury, patientName, patientNameMasked),
            subjectiveProgressSummary: R.redactPatientName(subjectiveProgressSummary, patientName, patientNameMasked)
        } : {
            behavioralAssessments,
            continuedNeedsAssessments,
            discoveries,
            goals,
            historyOfInjury,
            physicalAssessments,
            plan,
            socialAssessments,
            subjectiveProgressSummary
        };

        // Log continuedNeedsAssessments after redaction for debugging
        if (shouldRedactUserContent) {
            console.log('[validateReport] AFTER redaction - continuedNeedsAssessments:', {
                length: contentToValidate.continuedNeedsAssessments?.length,
                preview: contentToValidate.continuedNeedsAssessments?.substring(0, 200),
                containsName: contentToValidate.continuedNeedsAssessments?.includes(patientName),
                containsFirstName: contentToValidate.continuedNeedsAssessments?.includes(patientName.split(" ")[0]),
                containsMaskedName: contentToValidate.continuedNeedsAssessments?.includes(patientNameMasked)
            });
        }

        let isValid: boolean = R.validateReport({
            assessmentsGenerated,
            behavioralAssessments: contentToValidate.behavioralAssessments,
            continuedNeedsAssessments: contentToValidate.continuedNeedsAssessments,
            discoveries: contentToValidate.discoveries,
            goals: contentToValidate.goals,
            hasCoachingPlan,
            includeBehavioralAssessments,
            includeContinuedNeedsAssessments,
            includePhysicalAssessments,
            includeSocialAssessments,
            notificationManager,
            physicalAssessments: contentToValidate.physicalAssessments,
            plan: contentToValidate.plan,
            reportWeek,
            socialAssessments: contentToValidate.socialAssessments,
            subjectiveProgressSummary: contentToValidate.subjectiveProgressSummary
        });

        if (isValid && validatePatientNameMask) {
            // For VA patients:
            // - Auto-generated content (assessments, plan) is already redacted
            // - If auto-redact is enabled: validates that user-entered content redaction was successful
            // - If auto-redact is disabled: warns user if patient names are found in user-entered content
            return R.validatePatientName({
                behavioralAssessments: contentToValidate.behavioralAssessments,
                continuedNeedsAssessments: contentToValidate.continuedNeedsAssessments,
                discoveries: contentToValidate.discoveries,
                goals: contentToValidate.goals,
                historyOfInjury: contentToValidate.historyOfInjury,
                patientName: patientName,
                payerType,
                physicalAssessments: contentToValidate.physicalAssessments,
                plan: contentToValidate.plan,
                socialAssessments: contentToValidate.socialAssessments,
                subjectiveProgressSummary: contentToValidate.subjectiveProgressSummary
            });
        }

        return isValid;
    };

    const processMutableReportData = (
        isGenerator: boolean,
        report: RD.EditableReport,
        reportWeek: RD.ReportWeek,
        reportPlanWeeks: number
    ) => {
        RDP.processMutableReportData({
            isGenerator,
            report,
            reportWeek,
            reportPlanWeeks,
            // State Setters
            setAllTrajectories,
            setAssessmentsGenerated,
            setAverageActivityTimePerWeek,
            setBehavioralAssessments,
            setContinuedNeedsAssessments,
            setDiscoveries,
            setDiscoveriesOriginal,
            setEngagementLevel,
            setGoalsProgress,
            setGoals,
            setGoalsOriginal,
            setHistoryOfInjury,
            setHistoryOfInjuryOriginal,
            setHistoryOfInjuryCitation,
            setIncludeAverageTime,
            setIncludeBehavioralAssessments,
            setIncludeContinuedNeedsAssessments,
            setIncludeEngagementLevel,
            setIncludeGoalsProgress,
            setIncludeMedicationReduction,
            setIncludePhysicalAssessments,
            setIncludeReturnToWork,
            setIncludeSessionsAttended,
            setIncludeSocialAssessments,
            setIncludeUniqueDays,
            setIncludedTrajectories,
            setMedicationReductionStatus,
            setPhysicalAssessments,
            setPlan,
            setPlanOriginal,
            setPlanSettings,
            setReportPostProcessingTrigger,
            setReturnToWorkStatus,
            setSelectedImprovedTrajectoryTypes,
            setSessionsAttended,
            setSocialAssessments,
            setSocialAssessmentsOriginal,
            setSubjectiveProgressSummary,
            setSubjectiveProgressSummaryOriginal,
            setSubjectiveProgressSummaryCitation,
            setUniqueDays
        });
    };

    const processReport = (response: any) => {
        const responseData: RD.ReportDataApiResponse = response.data;

        if (!responseData) {
            setIsLoading(false);
            return;
        }

        let report: RD.EditableReport = responseData;

        const generatedData: RD.ReportGeneratedData = report?.generated_data;
        if (generatedData) {
            console.group(`📝 Processing Generated Data`);
            console.log(colorizeJson(report.generated_data));
            console.groupEnd();
        }

        const immutableReportData = RDP.processImmutableReportData({
            isGenerator,
            notificationManager,
            responseData,
            // State Setters
            setBrand,
            setClaimNumber,
            setCoach,
            setDateOfBirth,
            setDateOfInjury,
            setHasCoachingPlan,
            setHasPhysicianOversight,
            setIsReportConfigurationDirty,
            setPatientId,
            setPatientName,
            setPatientNameMasked,
            setPayerType,
            setPhysician,
            setPreparedDate,
            setPreparerName,
            setProgramType,
            setReport,
            setReportPlanWeeks,
            setReportWeek,
            setReportWeekOptions,
            setSamplePlanWeeks,
            setCitations
        });

        if (!immutableReportData) {
            return;
        }
        else {
            const [report, reportWeek, reportPlanWeeks] = immutableReportData;
            if (report !== undefined && reportWeek !== undefined && reportPlanWeeks !== undefined) {
                setReportPostProcessingReplaceAutoSavedData(false);
                processMutableReportData(
                    isGenerator,
                    report,
                    reportWeek,
                    reportPlanWeeks
                );
            }
            return [reportWeek, reportPlanWeeks];
        }
    };

    const confirmReportChangeWeek = () => {
        const text
            = `WARNING!\n\nAre you sure you want to change the report week?\n\n`
            +`Any edits you have made will be lost!`
        if (!window.confirm(text)) {
            return false;
        }

        // Revert all useDirtyState vars to their original values
        // before reprocessing the report for a new week. Any
        // regular useStart vars do not need to be reverted as
        // they are either immutable across changes in the report
        // week (e.g., Patient Info) or are guaranteed to be set
        // during report reprocessing (e.g., Report Configuration).
        dirtyManager.revertToOriginal();

        return true;
    }

    const processReportForWeek = (newReportWeek: RD.ReportWeek) => {
        notificationManager.clearAllNotifications();

        if (!confirmReportChangeWeek()) {
            return;
        }

        if (!report) {
            console.error("Cannot process report for week: Initial report data not loaded.");
            notificationManager.addError("Report data is not available. Please refresh the page and try again.");
            return;
        }

        dirtyManager.pause();

        // Calculate the plan weeks relevant for this specific report week
        let newReportPlanWeeks: number = R.isMonth6Report(newReportWeek)
            ? samplePlanWeeks // Use the number of plan weeks included in the sampled data
            : Number(newReportWeek); // Use the specific week number

        // Update the state for the selected week and relevant plan weeks.
        setReportPlanWeeks(newReportPlanWeeks);
        setReportWeek(newReportWeek);

        // When the report week is changed, replace any previously auto-saved data with only:
        //
        //   1. The new report week
        //   2. The number of available plan weeks in the report data for the new report week.
        //
        // The replacement will occur after the report data has been re-processed
        // for the newly selected week and all state changes have been marked clean.
        // After this point, the auto-saver will resume change detection.
        setReportPostProcessingReplaceAutoSavedData(true);

        processMutableReportData(
            isGenerator,
            report,
            newReportWeek,
            newReportPlanWeeks
        );
    };

    const dispatchChangeReportWeek = (newReportWeek: RD.ReportWeek) => {
        if (newReportWeek !== reportWeek) {
            processReportForWeek(newReportWeek);
        }
    };

    const checkoutReport = async () => {
        notificationManager.clearAllNotifications();
        setLoadingMessage("Retrieving Outcome Report");
        setIsLoading(true);

        // Pause the DirtyStateManager until all report data has been processed
        dirtyManager.pause();

        await API.api_Get(
            componentName,
            `/outcome_reports/${reportId}/checkout/?include_post_program_checkins=true`,
            notificationManager,
            (response) => {
                processReport(response);
            },
            (error) => {
                console.error("Error retrieving report:", error);
                notificationManager.addError(["Error retrieving report:", error]);
            },
            () => {
                setIsLoading(false);
            }
        );
    };

    // ---=== Report Data Generation ===--- //

    const generatedReportData = (): RD.ReportGeneratedData => {
        return ({
            // Report Configuration
            assessmentsGenerated: assessmentsGenerated,
            reportPlanWeeks: reportPlanWeeks,
            reportWeek: reportWeek,
            // History of Injury
            historyOfInjury: historyOfInjury,
            // Subjective Outcomes
            discoveries: discoveries,
            engagementLevel: engagementLevel,
            goals: goals,
            goalsProgress: goalsProgress,
            subjectiveProgressSummary: subjectiveProgressSummary,
            // Assessments
            behavioralAssessments: behavioralAssessments,
            continuedNeedsAssessments: continuedNeedsAssessments,
            physicalAssessments: physicalAssessments,
            socialAssessments: socialAssessments,
            // Plan
            plan: plan,
            planSettings: planSettings,
            // Inclusions
            inclusions: {
                // Key Improvements
                selectedImprovedTrajectoryTypes: selectedImprovedTrajectoryTypes,
                // Progress
                averageTime: includeAverageTime,
                engagementLevel: includeEngagementLevel,
                goalsProgress: includeGoalsProgress,
                medicationReduction: includeMedicationReduction,
                returnToWork: includeReturnToWork,
                sessionsAttended: includeSessionsAttended,
                uniqueDays: includeUniqueDays,
                // Trajectories
                trajectories: includedTrajectories,
                // Assessments
                behavioralAssessments: includeBehavioralAssessments,
                continuedNeedsAssessments: includeContinuedNeedsAssessments,
                physicalAssessments: includePhysicalAssessments,
                socialAssessments: includeSocialAssessments
            }
        });
    };

    const generatePdf = async (
        keyImprovementsChartImageData: string,
        downloadPdf: boolean,
        hideLoaderAfterDownload: boolean
    ) => {
        notificationManager.clearAllNotifications();
        setIsLoading(true);
        setLoadingMessage("Generating Outcome Report PDF");

        try {
            // For VA patients, optionally redact user-entered content before generating PDF
            // Auto-generated content (assessments, plan, header) is already redacted at generation time
            // This behavior is controlled by VITE_OUTCOME_REPORTS_AUTO_REDACT env var
            const shouldRedactUserContent = payerType === RD.PayerEnums.VA && RD.isOutcomeReportsAutoRedactEnabled();

            // Auto-generated content - passed as-is (already redacted at generation time)
            const redactedBehavioralAssessments = behavioralAssessments;
            const redactedPhysicalAssessments = physicalAssessments;
            const redactedSocialAssessments = socialAssessments;
            const redactedContinuedNeedsAssessments = continuedNeedsAssessments;
            const redactedPlan = plan;

            // User-entered content - redact if auto-redact is enabled
            const redactedSubjectiveSummary = shouldRedactUserContent ? R.redactPatientName(subjectiveProgressSummary, patientName, patientNameMasked) : subjectiveProgressSummary;
            const redactedGoals = shouldRedactUserContent ? R.redactGoalsPatientName(goals, patientName, patientNameMasked) : goals;
            const redactedDiscoveries = shouldRedactUserContent ? R.redactDiscoveriesPatientName(discoveries, patientName, patientNameMasked) : discoveries;
            const redactedHistoryOfInjury = shouldRedactUserContent ? R.redactPatientName(historyOfInjury, patientName, patientNameMasked) : historyOfInjury;

            const pdfDoc = await RP.generatePdf({
                // Report Configuration
                brand,
                preparedDate,
                preparerName,
                programType,
                reportPlanWeeks,
                reportWeek,
                // Patient Info
                claimNumber,
                coach,
                dateOfBirth,
                dateOfInjury,
                hasPhysicianOversight,
                patientId,
                patientName: patientNameMasked,
                payerType,
                physician,
                // Key Improvements Chart
                keyImprovementsChartImageData,
                // History of Injury
                historyOfInjury: redactedHistoryOfInjury,
                // Subjective Outcomes
                discoveries: redactedDiscoveries,
                engagementLevel,
                goalsProgress,
                goals: redactedGoals,
                subjectiveProgressSummary: redactedSubjectiveSummary,
                // Objective Outcomes
                allTrajectories,
                averageActivityTimePerWeek,
                medicationReductionStatus,
                returnToWorkStatus,
                sessionsAttended,
                uniqueDays,
                // Assessments
                behavioralAssessments: redactedBehavioralAssessments,
                continuedNeedsAssessments: redactedContinuedNeedsAssessments,
                physicalAssessments: redactedPhysicalAssessments,
                socialAssessments: redactedSocialAssessments,
                // Plan
                plan: redactedPlan,
                // Inclusions
                includeAverageTime,
                includeBehavioralAssessments,
                includeContinuedNeedsAssessments,
                includeEngagementLevel,
                includeGoalsProgress,
                includeMedicationReduction,
                includePhysicalAssessments,
                includeReturnToWork,
                includeSessionsAttended,
                includeSocialAssessments,
                includeUniqueDays,
                includedTrajectories,
                selectedImprovedTrajectoryTypes
            });

            if (downloadPdf) {
                pdfDoc.save(RP.generateReportPdfFileName(patientNameMasked, reportWeek, true, null, patientId, payerType));
                if (hideLoaderAfterDownload) {
                    setIsLoading(false);
                }
            }

            return pdfDoc;
        }
        catch (error) {
            console.error(error);
            notificationManager.addError(["Error generating Outcome Report PDF! Contact a Karuna Admin.", error]);
        }
    };

    // ---=== Report Operations ===--- //

    const createReport = async () => {
        notificationManager.clearAllNotifications();
        setLoadingMessage("Collecting Outcome Report Data");
        setIsLoading(true);

        // Pause the DirtyStateManager until all report data has been processed
        dirtyManager.pause();

        try {
            const result: ApiCallResult = await API.api_Post(
                componentName,
                "/outcome_reports/",
                { patient_id: patientId, include_post_program_checkins: true },
                notificationManager
            );

            if (!result.wasSuccessful) {
                return;
            }

            processReport(result.response);
        }
        catch (error: any) {
            console.error("Error creating Outcome Report:", error);
            notificationManager.addError(["Error creating Outcome Report:", error]);
        }
        finally {
            setIsLoading(false);
        }
    };

    const autoSaveReport = async (
        autoSaveGeneratedReportData: RD.ReportGeneratedData,
        replaceAutoSavedData: boolean = false
    ) => {
        if (!autoSaveGeneratedReportData) {
            return;
        }

        dirtyManager.pause();

        if (replaceAutoSavedData) {
            console.warn("Replacing existing auto-saved report data.");
            setAutoSaveStatus(AutoSaveStatus.InProgress);
        }

        console.group(`📝 Committing Patch Data`);
        console.log(colorizeJson(autoSaveGeneratedReportData));
        console.groupEnd();

        try {
            const result: ApiCallResult = await API.api_Patch(
                componentName,
                `/outcome_reports/${report.id}/commit/`,
                { generated_data: autoSaveGeneratedReportData, replace: replaceAutoSavedData },
                notificationManager
            );

            if (!result.wasSuccessful) {
                return false;
            }

            dirtyManager.markAutoSaveComplete();

            if (replaceAutoSavedData) {
                setAutoSaveStatus(AutoSaveStatus.Idle);
            }
            else {
                // If auto-save data has not been replaced, a check is performed to see if
                // any new dirty state has been added to the DirtyStateManager. If so, set the
                // UI state to Pending to indicate a further auto save is pending.
                if (dirtyManager.isDirty()) {
                    // If it's still dirty, new changes are waiting. Move the UI back to 'Pending'.
                    setAutoSaveStatus(AutoSaveStatus.Pending);
                }
            }

            return true;
        }
        catch (error) {
            console.error(error);
            notificationManager.addError(error);
            return false;
        }
        finally {
            dirtyManager.resume();

            if (dirtyManager.isForcedAutoSaveInProgress()) {
                dirtyManager.completeForcedAutoSave(true);
            }
        }
    };

    const autoSaveReportBeforeNavigation = useCallback(async (canRetry: boolean = true) => {
        console.info("autoSaveReportBeforeNavigation START");

        if (!dirtyManager.isDirty()) {
            console.info(`navigationAutoSave END: no dirty state found requiring forced auto-save.`);
            return true; // Allow navigation if there is nothing to save
        }

        setIsLoading(true);
        setLoadingMessage("Auto-saving Outcome Report");

        let autoSaveSuccess = false;

        try {
            console.warn("Dirty state found! Forcing immediate auto-save.");
            autoSaveSuccess = await dirtyManager.forceAutoSave();
            console.warn(`Forced auto-save complete: success[${autoSaveSuccess}]`);
        }
        catch (error) {
            console.error("Auto-save failed:", error);
        }
        finally {
            if (!autoSaveSuccess) {
                if (canRetry) {
                    notificationManager.addError("Auto-save failed preventing navigation to another page. Fix any errors and try again.");
                    setIsLoading(false);
                }
            }
        }

        console.info(`autoSaveReportBeforeNavigation END: forceAutoSave success[${autoSaveSuccess}]`);

        return autoSaveSuccess;
    }, [dirtyManager]);

    const { disable: disableNavigationBlocker } = useNavigationBlocker({ onNavigationAttempt: autoSaveReportBeforeNavigation, useBlocker: useBlocker });

    const submitForReview = async () => {
        notificationManager.clearAllNotifications();
        setLoadingMessage("Auto-saving Outcome Report");
        setIsLoading(true);
        setValidationEnabled(true);

        const autoSaveSuccess = await dirtyManager.forceAutoSave();

        if (!autoSaveSuccess) {
            notificationManager.addError("Auto-save failed preventing submission of the report. Please try again.");
            setIsLoading(false);
            return;
        }

        dirtyManager.pause();

        try {
            if (!validateReport(true)) {
                throw new Error("Report validation failed.");
            }

            setLoadingMessage("Submitting Outcome Report for Review");

            await API.api_Patch(
                componentName,
                `/outcome_reports/${report.id}/submit/`,
                { generated_data: generatedReportData() },
                notificationManager,
                (response) => {
                    let successMessage = `Submitted for Review: ${patientNameMasked} ${R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${Number(reportWeek)}`} Outcome Report`;
                    notificationManager.addSuccess(successMessage, { duration: 10 });
                    void navigate(`/patient/${patientId}/reports`);
                },
                (error) => {
                    dirtyManager.resume();
                    console.error(error);
                    notificationManager.addError(error);
                    setIsLoading(false);
                }
            );
        }
        catch (error) {
            dirtyManager.resume();
            setIsLoading(false);
        }
    };

    const previewReport = async () => {
        setValidationEnabled(true);
        const keyImprovementsChartImageData = chartRef.current?.toBase64Image();

        if (!validateReport(true)) {
            return;
        }

        const pdfDoc = await generatePdf(keyImprovementsChartImageData, true, true);

        if (!pdfDoc) {
            notificationManager.addError("PDF generation failed. Unable to preview report. Please try again.");
        }
    };

    const approveReport = async () => {
        const keyImprovementsChartImageData = chartRef.current?.toBase64Image();

        notificationManager.clearAllNotifications();
        setLoadingMessage("Auto-saving Outcome Report");
        setIsLoading(true);
        setValidationEnabled(true);

        const autoSaveSuccess = await dirtyManager.forceAutoSave();

        if (!autoSaveSuccess) {
            notificationManager.addError("Auto-save failed preventing approval of the report. Please try again.");
            setIsLoading(false);
            return;
        }

        dirtyManager.pause();

        try {
            if (!validateReport(true)) {
                throw new Error("Report validation failed.");
            }

            setLoadingMessage("Approving Outcome Report");

            const pdfDoc = await generatePdf(keyImprovementsChartImageData, false, false);

            if (!pdfDoc) {
                const errorMsg = "PDF generation failed. Unable to approve report. Please try again.";
                notificationManager.addError(errorMsg);
                throw new Error(errorMsg);
            }

            await API.api_Patch(
                componentName,
                `/outcome_reports/${report.id}/approve/`,
                { generated_data: generatedReportData(), pdf_data: pdfDoc.output("datauristring") },
                notificationManager,
                (response) => {
                    const successMessage = `Approved: ${patientNameMasked} ${R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${Number(reportWeek)}`} Outcome Report`;
                    notificationManager.addSuccess(successMessage, { duration: 10 });
                    void navigate("/reports");
                },
                (error) => {
                    dirtyManager.resume();
                    console.error(error);
                    notificationManager.addError(error);
                    setIsLoading(false);
                }
            )
        }
        catch (error) {
            dirtyManager.resume();
            setIsLoading(false);
        }
    };

    const deleteReport = async () => {
        notificationManager.clearAllNotifications();
        setLoadingMessage("Auto-saving Outcome Report");
        setIsLoading(true);

        const autoSaveSuccess = await dirtyManager.forceAutoSave();

        if (!autoSaveSuccess) {
            notificationManager.addError("Auto-save failed preventing deletion of the report. Please try again.");
            setIsLoading(false);
            return;
        }

        dirtyManager.pause();

        try {
            setLoadingMessage("Deleting Outcome Report");

            await API.api_Delete(
                componentName,
                `/outcome_reports/${report.id}/`,
                notificationManager,
                (_response) => {
                    let successMessage = `Deleted: ${patientNameMasked} ${R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${Number(reportWeek)}`} Outcome Report`;
                    notificationManager.addSuccess(successMessage, { duration: 10 });

                    if (isGenerator) {
                        void navigate(`/patient/${patientId}/reports`);
                    }
                    else {
                        void navigate("/reports");
                    }
                },
                (error) => {
                    dirtyManager.resume();
                    notificationManager.addError(error);
                    setIsLoading(false);
                }
            );
        }
        catch (error) {
            dirtyManager.resume();
            notificationManager.addError(error);
            setIsLoading(false);
        }
    };

    const deleteReportConfirm = () => {
        let text = "WARNING!\n\nAre you sure you want to delete this Outcome Report?\n\nThere is no way to recover it once deleted!\n\nPress OK to delete or Cancel to return to editing.";
        if (window.confirm(text) === true) {
            void deleteReport();
        }
    };

    // ---=== Logout Hook ===--- //

    const logoutHook: LogoutHook = async (_componentName, _logoutReason, onLogoutHookAccept, onLogoutHookReject) => {
        notificationManager.clearAllNotifications();
        disableNavigationBlocker();

        const saveWasSuccessful: boolean = await autoSaveReportBeforeNavigation(false);

        const isSessionTimeout = _logoutReason?.includes("session timed out");

        if (!saveWasSuccessful) {
            const errorMsg = `Auto-save failed during ${isSessionTimeout ? "session timeout" : "logout"}. The last few seconds of editing may have been lost.`;
            console.error(errorMsg);
            notificationManager.addError(isSessionTimeout
                ? "Your session timed out. Please login again.\nUnfortunately, your last few seconds of\nreport edits could not be saved."
                : "Logging out.\nUnfortunately, your last few seconds of\nreport edits could not be saved.")
        }
        else {
            notificationManager.addSuccess(isSessionTimeout
                ? "Your session timed out. Please login again.\nAll report edits were saved."
                : "Logging out.\nAll report edits were saved.")
        }

        onLogoutHookAccept();
    };

    // ---=== useEffects ===--- //

    // ---=== Component Mounting ===--- //

    useEffect(
        () => {
            if (!reportId) {
                void createReport();
            }
            else {
                void checkoutReport();
            }
        }, []
    );

    // ---=== Notifications ===--- //

    useEffect(
        () => {
            if (!isLoading && validationEnabled) {
                if (validateReport(false)) {
                    if (isGenerator) {
                        notificationManager.addSuccess("Report is ready for submission.");
                    }
                    else {
                        notificationManager.addSuccess("Report is ready for preview and approval.");
                    }
                }
            }
        }, [
            isLoading,
            assessmentsGenerated,
            behavioralAssessments,
            continuedNeedsAssessments,
            discoveries,
            goals,
            hasCoachingPlan,
            includeBehavioralAssessments,
            includeContinuedNeedsAssessments,
            includePhysicalAssessments,
            includeSocialAssessments,
            physicalAssessments,
            plan,
            reportWeek,
            socialAssessments,
            subjectiveProgressSummary,
            validationEnabled
        ]
    );

    // ---=== DirtyStateManager & Auto-Save ===--- //

    useEffect(
        () => {
            // Start in the paused state for the first report data load
            dirtyManager.pause();
            dirtyManager.setDebugMode(DebugLevel.INFO);
            setIsDirtyStateManagerInitialized(true);

            // Cleanup function when the component unmounts
            return () => {
                // console.log("DirtyStateManager: Clearing config.");
            }
        }, [dirtyManager]
    );

    useEffect(
        () => {
            if (reportPostProcessingTrigger > 0) {
                if (reportPostProcessingReplaceAutoSavedData) {
                    void autoSaveReport(
                        {
                            reportWeek: reportWeek,
                            reportPlanWeeks: reportPlanWeeks
                        },
                        true
                    );
                }

                console.warn("Report Processing Complete: Marking all clean.");
                dirtyManager.markClean();
                dirtyManager.resume();
            }
        },
        [reportPostProcessingTrigger, reportPostProcessingReplaceAutoSavedData]
    );

    useEffect(
        () => {
            if (report) {
                createHeaderConfig();
            }
        }, [autoSaveStatus, createHeaderConfig, patientName, report, reportWeek]
    );

    useEffect(
        () =>{
            if (autoSaveGeneratedData) {
                let autoSaveGeneratedDataCopy: RD.ReportGeneratedData;
                if (isReportConfigurationDirty) {
                    console.warn("Augmenting auto-save generated date with dirty report configuration.");
                    setIsReportConfigurationDirty(false);
                    autoSaveGeneratedDataCopy = {
                        reportWeek: reportWeek,
                        reportPlanWeeks: reportPlanWeeks,
                        ...autoSaveGeneratedData
                    }
                }
                else {
                    autoSaveGeneratedDataCopy = { ...autoSaveGeneratedData };
                }

                setAutoSaveGeneratedData(null);

                void autoSaveReport(autoSaveGeneratedDataCopy, false);
            }
        },
        [autoSaveGeneratedData, isReportConfigurationDirty]
    );

    useEffect(
        () => {
            if (assessmentsGenerated) {
                if (assessmentsDividerRef.current) {
                    assessmentsDividerRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }
        },
        [assessmentsGenerated]
    );

    // useEffect(
    //     () => {
    //         if (triggerSaveReport > 0) {
    //             void saveReportConfirm();
    //         }
    //     },
    //     [triggerSaveReport]
    // );

    useEffect(
        () => {
            if (triggerSubmitForReview > 0) {
                void submitForReview();
            }
        },
        [triggerSubmitForReview]
    );

    useEffect(
        () => {
            if (triggerDeleteReportWarning > 0) {
                void deleteReportConfirm();
            }
        },
        [triggerDeleteReportWarning]
    );

    useEffect(
        () => {
            if (triggerApproveReport > 0) {
                void approveReport();
            }
        },
        [triggerApproveReport]
    );

    useEffect(
        () => {
            if (triggerPreviewPdf > 0) {
                void previewReport();
            }
        },
        [triggerPreviewPdf]
    );

    // Regenerate assessment decisions whenever trajectories or inclusions change
    // This ensures the "View Assessment Determinations" button works for existing reports
    useEffect(
        () => {
            if (allTrajectories.length > 0 && includedTrajectories) {
                const results = generateAssessmentsUtil(allTrajectories, includedTrajectories);
                setAssessmentDecisions(results.decisions);
            }
        }, [allTrajectories, includedTrajectories]
    );

    // Handle ESC key to close Assessment Determinations modal
    useEffect(
        () => {
            const handleEscKey = (event: KeyboardEvent) => {
                if (event.key === 'Escape' && showAssessmentDeterminationsModal) {
                    setShowAssessmentDeterminationsModal(false);
                }
            };

            document.addEventListener('keydown', handleEscKey);
            return () => {
                document.removeEventListener('keydown', handleEscKey);
            };
        }, [showAssessmentDeterminationsModal]
    );

    // ---=== Render ===--- //

    const getModularHeaderConfig = (): ModularHeaderConfig => {
        return {
            trail: breadCrumbLinks(isGenerator)
        };
    };

    const breadCrumbLinks = (isGenerator: boolean) => {
        return isGenerator
            ? (
                [
                    { label: "Patients", url: "/" }
                ]
            )
            : (
                [
                    { label: "Reports", url: `/reports` }
                ]
            )
    };

    const createRenderKeyImprovedTrajectoryPayload = (trajectory: T.Trajectory) => {
        let measureType: MD.MeasureType = trajectory.measureType;
        let included = T.hasTrajectoryOfType(measureType, selectedImprovedTrajectoryTypes);
        return {
            trajectory: trajectory,
            included,
            disabled: (isGenerator && assessmentsGenerated) || (selectedImprovedTrajectoryTypes?.length >= 5 && !included),
            assessmentDecisions,
            reportWeek,
            toggleTrajectoryCallback: toggleKeyImprovedTrajectoryCallback
        } as RD.RenderKeyImprovedTrajectoryPayload;
    };

    const renderKeyImprovedTrajectoriesSection = () => {
        let renderPayload: RD.RenderKeyImprovedTrajectoriesPayload = allTrajectories.map(
            (trajectory: T.Trajectory) => {
                if (trajectory.bandSeverityTrajectory !== T.TrajectoryType.IMPROVED) {
                    return null;
                }
                return createRenderKeyImprovedTrajectoryPayload(trajectory);
            }
        );
        renderPayload = renderPayload.filter(payload => payload !== null);

        return R.renderKeyImprovedTrajectoriesSection({
            isGenerator,
            assessmentsGenerated,
            renderPayload,
            reportWeek,
            citations
        });
    };

    const renderReport = () => {
        return (
            <div className={"w-full h-full p-4 flex-col gap-y-1 justify-start items-center whitespace-nowrap overflow-x-hidden"}>
                {
                    R.renderReportHeader({
                        hasPhysicianOversight: hasPhysicianOversight,
                        isGenerator: isGenerator,
                        reportWeek: reportWeek,
                        reportWeekOptions: reportWeekOptions,
                        // Callbacks
                        reportWeekChangeCallback: dispatchChangeReportWeek
                    })
                }
                <ReportSectionDivider key={`divider-key-improved-trajectories`} label={"Key Improvements"} locked={true}>
                    {
                        T.hasTrajectories(allTrajectories)
                            ? (
                                <React.Fragment key={`content-key-improved-trajectories`}>
                                    <div id="key-improved-trajectories" className="flex flex-col w-full justify-center items-center">
                                        <div id="key-improved-trajectories-graph-header" className="inline-flex justify-center items-center gap-3 m-2 p-2 border-1 border-gray-400 rounded-md">
                                            <img className="w-6 h-6 relative" src="/icons/percent-03.svg" alt="icon"/>
                                            <div className="grow shrink basis-0 flex-col justify-start items-start text-wrap">
                                                <div className="self-stretch text-wrap text-gray-600 text-base max-[1200px]:text-sm font-normal leading-normal">
                                                    When displayed in the graph, measures are converted to a standard percentage score, where higher is better.
                                                </div>
                                            </div>
                                        </div>
                                        <div id="key-improved-trajectories-graph" className="w-fit items-center">
                                            {
                                                renderKeyImprovedTrajectoriesGraph(chartRef, reportWeek, reportPlanWeeks, allTrajectories, selectedImprovedTrajectoryTypes, customReportColors)
                                            }
                                        </div>
                                        <div id="key-improved-trajectories-header" className="w-full grid grid-cols-[1fr_auto_1fr] items-center mt-3 mb-2">
                                            <div id="left-content-lock-icon" className="flex justify-end pr-1 items-center">
                                                {
                                                    isGenerator && assessmentsGenerated && (
                                                        <img className="w-5 h-5 translate-y-[-1px]" src="/icons/WarningLock.svg" alt="lock icon" />
                                                    )
                                                }
                                            </div>
                                            <div id="center-content-title" className="flex justify-center items-center text-gray-900 text-lg font-semibold leading-8">
                                                Included Measures
                                            </div>
                                            <div id="right-content-help" className="flex justify-start pl-1 items-center gap-1 whitespace-nowrap">
                                                <span className="relative">
                                                    <div className="has-tooltip flex items-center gap-1">
                                                        <img className="h-5 w-5 relative hs-tooltip" src="/icons/help-circle.svg" alt="help icon" />
                                                        <span className="tooltip rounded shadow-lg whitespace-normal break-normal max-w-[30vw] w-max mb-2 ml-2">
                                                            To qualify for the Key Improvements graph, a measure must have improved into a higher score band since its first measurement.
                                                        </span>
                                                    </div>
                                                </span>
                                                <div className="text-gray-400 text-xs font-normal leading-[18px]">
                                                    {`${selectedImprovedTrajectoryTypes ? selectedImprovedTrajectoryTypes.length : 0}/5 included`}
                                                </div>
                                            </div>
                                        </div>
                                        {
                                            renderKeyImprovedTrajectoriesSection()
                                        }
                                    </div>
                                </React.Fragment>
                            )
                            : (
                                <React.Fragment key={`content-key-no-improved-trajectories`}>
                                    <div id="no-key-improved-trajectories" className="flex flex-col w-full justify-center items-center">
                                        <div className="justify-start items-start gap-3 inline-flex">
                                            <img id="chart" className="w-6 h-6 relative overflow-hidden" src="/icons/noKeyImp.svg" alt="icon"/>
                                            <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
                                                <div className="self-stretch text-gray-600 text-base font-normal leading-normal">
                                                    Patient has not made sufficient progress in any outcome measure to include a Key Improvements graph.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                    }
                </ReportSectionDivider>
                <ReportSectionDivider key={`divider-patient-history`} label={"Patient History"}>
                    <div id="patient-history" className="flex flex-col w-full justify-center items-center gap-4 p-5">
                        {/* --- History of Injury --- */}
                        <div className="w-[80%]">
                            <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0 flex justify-between items-center">
                                <span>History of Injury</span>
                                {RDP.ENABLE_CITATIONS_DISPLAY && <CitationDisplay citations={citations} citationIndex={historyOfInjuryCitation} />}
                            </div>
                            <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                    Write a short summary of the Nature and Mechanism of the patient’s injury.
                                </div>
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                    <div className="w-full gap-2">
                                        <ManagedTextArea
                                            id="history-of-injury-input"
                                            className="w-full resize-none h-[15vh] p-2 bg-white rounded-lg shadow border border-gray-300 justify-start items-start gap-2 inline-flex"
                                            placeholder="Enter History"
                                            // useDirtyState props
                                            value={historyOfInjury}
                                            updateValue={updateHistoryOfInjury}
                                        />
                                        {
                                            !isGenerator && (
                                                <span className="text-gray-600 flex items-center gap-1">
                                                    <button
                                                        className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border cursor-pointer"
                                                        onClick={e => {e.preventDefault(); dispatchRevertHistoryOfInjury();}}
                                                    >
                                                        <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                    </button>
                                                    <span className="whitespace-nowrap">
                                                        Restore Unedited History of Injury
                                                    </span>
                                                </span>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                        </div>
                    </div>
                </ReportSectionDivider>
                <ReportSectionDivider key={`divider-subjective-outcomes`} label={"Subjective Outcomes"}>
                    <div id="subjective-outcomes" className="flex flex-col w-full justify-center items-center gap-4 p-5">
                        {/* --- Patient Goals --- */}
                        <ReportGoalsManager goals={goals} originalGoals={goalsOriginal} citations={citations} showRestoreButton={true} updateGoals={updateGoals}/>
                        {/* --- Patient Perspective --- */}
                        <div id="patient-perspective" className="w-[80%]">
                            <div id="patient-perspective-trajectories-grid" className="grid grid-cols-[max-content_max-content_max-content_1fr] gap-x-0 gap-y-0 bg-gray-25">
                                <div className="col-span-4 pl-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-t border-l border-r shadow-sm border-gray-300 rounded-t-md mt-4 first:mt-0">
                                    Patient Perspective
                                </div>
                                <div className="col-span-4 h-3 border-l border-r border-gray-300"></div>
                                <div className="contents">
                                    <div className={`${R.trajectoryIncluderCellClasses} border-l shadow-sm border-gray-300 col-start-1`}>
                                        <label className={`justify-start items-center flex gap-x-2 text-base font-normal leading-normal ${goalsProgress === null ? "text-gray-400 " : "text-gray-600 "}`}>
                                            <ManagedInput
                                                id={"perceived-progress-towards-goals"}
                                                className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                type="checkbox"
                                                value={includeGoalsProgress}
                                                updateValue={checked => setIncludeGoalsProgress(checked)}
                                                disabled={goalsProgress === null}
                                            />
                                            Perceived Progress Towards Goals
                                        </label>
                                    </div>
                                    <div className={`${R.trajectoryValueCellClasses} col-start-2`}>
                                        {
                                            goalsProgress?.value != null ? `${goalsProgress.value.toString()}/10` : R.renderNoTrajectoryValueDecorator()
                                        }
                                    </div>
                                    <div className={`${R.trajectoryWeekCellClasses} mr-4 col-start-3`}>
                                        {
                                            goalsProgress && goalsProgress.week != reportWeek &&
                                            R.renderBandTrajectoryWeekDecorator(goalsProgress.week)
                                        }
                                    </div>
                                    <div className={`ml-[-1px] border-r border-gray-300 col-start-4 flex justify-end items-center pr-4`}>
                                        {RDP.ENABLE_CITATIONS_DISPLAY && goalsProgress && <CitationDisplay citations={citations} citationIndex={goalsProgress.citation} />}
                                    </div>
                                </div>
                                <div className="contents">
                                    <div className={`${R.trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                                        <label className={`justify-start items-center flex gap-x-2 text-base font-normal leading-normal ${engagementLevel === null ? "text-gray-400" : "text-gray-600"}`}>
                                            <ManagedInput
                                                id={"level-of-engagement"}
                                                className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                type="checkbox"
                                                value={includeEngagementLevel}
                                                updateValue={checked => setIncludeEngagementLevel(checked)}
                                                disabled={engagementLevel === null}
                                            />
                                            Level of Engagement in Program
                                        </label>
                                    </div>
                                    <div className={`${R.trajectoryValueCellClasses} col-start-2`}>
                                        {
                                            engagementLevel?.value != null ? `${engagementLevel.value.toString()}/10` : R.renderNoTrajectoryValueDecorator()
                                        }
                                    </div>
                                    <div className={`${R.trajectoryWeekCellClasses} mr-4 col-start-3`}>
                                        {
                                            engagementLevel && engagementLevel.week != reportWeek &&
                                            R.renderBandTrajectoryWeekDecorator(engagementLevel.week)
                                        }
                                    </div>
                                    <div className={`ml-[-1px] border-r border-gray-300 col-start-4 flex justify-end items-center pr-4`}>
                                        {RDP.ENABLE_CITATIONS_DISPLAY && engagementLevel && <CitationDisplay citations={citations} citationIndex={engagementLevel.citation} />}
                                    </div>
                                </div>
                                <div className="col-span-4 h-3 border-b border-l border-r shadow-sm border-gray-300 rounded-b-md"></div>
                            </div>
                        </div>
                        {/* --- Patient Discoveries --- */}
                        <ReportDiscoveriesManager discoveries={discoveries} originalDiscoveries={discoveriesOriginal} citations={citations} showRestoreButton={true} updateDiscoveries={updateDiscoveries}/>
                        {/* --- Subjective Progress Summary --- */}
                        <div id="subjective-progress-summary" className="w-[80%]">
                            <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r rounded-t-md shadow-sm mt-4 first:mt-0 flex justify-between items-center">
                                <span>Subjective Progress Summary</span>
                                {RDP.ENABLE_CITATIONS_DISPLAY && <CitationDisplay citations={citations} citationIndex={subjectiveProgressSummaryCitation} />}
                            </div>
                            <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                    Write a short summary of the patient's engagement with the program and improvements in any area of their lives.
                                    <br/>
                                    Focus on the patient's own perspective and the progress they have communicated to you.
                                </div>
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                    <div className="w-full gap-2">
                                        <style>
                                            {`
                                                @keyframes blue-pulse {
                                                    0%, 100% {
                                                        background-color: white;
                                                        border-color: #d1d5db;
                                                    }
                                                    50% {
                                                        background-color: #eff6ff;
                                                        border-color: #93c5fd;
                                                    }
                                                }
                                                .generating-animation {
                                                    animation: blue-pulse 2s ease-in-out infinite;
                                                }
                                                .generation-complete {
                                                    animation: fade-to-white 0.6s ease-out forwards;
                                                    animation-fill-mode: forwards;
                                                }
                                                @keyframes fade-to-white {
                                                    from {
                                                        background-color: #eff6ff;
                                                        border-color: #93c5fd;
                                                    }
                                                    to {
                                                        background-color: white;
                                                        border-color: #d1d5db;
                                                    }
                                                }
                                            `}
                                        </style>
                                        <ManagedTextArea
                                            id="subjective-progress-summary-input"
                                            className={`w-full resize-none p-2 rounded-lg shadow border justify-start items-start inline-flex ${isGeneratingSubjectiveSummary ? 'generating-animation border-gray-300' : 'border-gray-300'}`}
                                            placeholder={isGeneratingSubjectiveSummary ? "Generating..." : "Enter Summary"}
                                            rows={10}
                                            disabled={isGeneratingSubjectiveSummary}
                                            // useDirtyState props
                                            value={subjectiveProgressSummary}
                                            updateValue={updateSubjectiveProgressSummary}
                                        />
                                        <div className="flex justify-between items-center gap-2 mt-2">
                                            {
                                                !isGenerator && (
                                                    <span className="text-gray-600 flex items-center gap-1">
                                                        <button
                                                            className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border cursor-pointer"
                                                            onClick={e => {e.preventDefault(); dispatchRevertSubjectiveProgressSummary()}}
                                                        >
                                                            <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                        </button>
                                                        <span className="whitespace-nowrap">
                                                            Restore Unedited Progress Summary
                                                        </span>
                                                    </span>
                                                )
                                            }
                                            {/*<div className="ml-auto">*/}
                                            {/*    {*/}
                                            {/*        cindraEnabled &&*/}
                                            {/*            <CindraSubjectiveProgressSummary*/}
                                            {/*                patientFullName={patientName}*/}
                                            {/*                isGenerator={isGenerator}*/}
                                            {/*                onSummaryGenerated={(summary) => updateSubjectiveProgressSummary(summary)}*/}
                                            {/*                onGenerationStart={() => setIsGeneratingSubjectiveSummary(true)}*/}
                                            {/*                onGenerationComplete={() => setIsGeneratingSubjectiveSummary(false)}*/}
                                            {/*            />*/}
                                            {/*    }*/}
                                            {/*</div>*/}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                        </div>
                    </div>
                </ReportSectionDivider>
                <ReportSectionDivider key={`divider-objective-outcomes`} label={"Objective Outcomes"} toggle={isGenerator && toggleObjectiveOutcomesSection} warningLock={isGenerator && assessmentsGenerated}>
                    <div id="objective-outcomes" className="flex flex-col w-full justify-center items-center p-5">
                        {
                            isGenerator && assessmentsGenerated && (
                                <React.Fragment key={`content-objective-outcomes`}>
                                    <div className="flex items-center gap-3 mx-2 mt-2 mb-4 p-2 bg-yellow-50 border border-orange-500 rounded">
                                        <img className="w-[18px] h-[18px] relative bg-gray-50 rounded-full" src="/icons/infoOrange.svg" alt="icon"/>
                                        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex text-wrap">
                                            <div className="self-stretch text-wrap text-gray-600 text-base max-[1200px]:text-sm font-normal leading-normal">
                                                <span
                                                    dangerouslySetInnerHTML={
                                                        {
                                                            __html: `Editing included measures will regnerate the Assessments section<br/>`
                                                                + `Any edits to the Assessments will be lost.`
                                                        }
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-start items-start h-11 gap-4 mb-4">
                                        <button
                                            className="self-stretch px-8 py-2.5 bg-warning-600 hover:bg-warning-800 border-warning-600 hover:border-warning-600 rounded-lg shadow justify-center items-center gap-1.5 inline-flex cursor-pointer"
                                            onClick={event => {event.preventDefault(); confirmAssessmentsReset()}}
                                        >
                                            <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/unlocked.svg" alt="icon"/>
                                                <div className="text-white text-base font-semibold leading-normal">
                                                    Edit Included Measures
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                </React.Fragment>
                            )
                        }
                        {
                            <R.RenderObjectiveOutcomeTrajectoriesGrid
                                allTrajectories={allTrajectories}
                                assessmentsGenerated={assessmentsGenerated}
                                includeAverageTime={includeAverageTime}
                                includeMedicationReduction={includeMedicationReduction}
                                includeReturnToWork={includeReturnToWork}
                                includeSessionsAttended={includeSessionsAttended}
                                includeUniqueDays={includeUniqueDays}
                                includedTrajectories={includedTrajectories}
                                isGenerator={isGenerator}
                                reportWeek={reportWeek}
                                sessionsAttended={sessionsAttended}
                                uniqueDays={uniqueDays}
                                averageActivityTimePerWeek={averageActivityTimePerWeek}
                                returnToWorkStatus={returnToWorkStatus}
                                medicationReductionStatus={medicationReductionStatus}
                                assessmentDecisions={assessmentDecisions}
                                citations={citations}
                                // useCallback
                                setIncludeAverageTime={setIncludeAverageTime}
                                setIncludeMedicationReduction={setIncludeMedicationReduction}
                                setIncludeReturnToWork={setIncludeReturnToWork}
                                setIncludeSessionsAttended={setIncludeSessionsAttended}
                                setIncludeUniqueDays={setIncludeUniqueDays}
                                toggleIncludedTrajectoryCallback={toggleIncludedTrajectoryCallback}
                            />
                        }
                    </div>
                </ReportSectionDivider>
                <ReportSectionDivider key={`divider-assessments`} id={"assessments-divider"} ref={assessmentsDividerRef} label={"Assessments"}>
                    {
                        isGenerator && !assessmentsGenerated
                            ? (
                                <React.Fragment key={`content-assessments`}>
                                    <div id="assessments-generate" className="flex flex-col w-full justify-center items-center">
                                        <div className="flex items-center gap-3 mx-2 mt-2 mb-4 p-2 bg-yellow-50 border border-orange-500 rounded">
                                            <img className="w-[18px] h-[18px] relative bg-gray-50 rounded-full" src="/icons/infoOrange.svg" alt="icon"/>
                                            <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex text-wrap">
                                                <div className="self-stretch text-wrap text-gray-600 text-base max-[1200px]:text-sm font-normal leading-normal">
                                                    Choose which Objective Outcome measures to include in the report <b>BEFORE</b> generating Assessments.<br/>
                                                    Changing included measures after generation will cause your edited Assessments to be lost.
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col justify-start items-start h-11 gap-4 mb-4">
                                            <button
                                                className="self-stretch px-8 py-2.5 bg-brand-600 hover:bg-brand-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex cursor-pointer"
                                                onClick={event => {event.preventDefault(); generateAssessments()}}
                                            >
                                                <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                    <img src="/icons/list.svg" className="w-5 h-5 relative" alt="icon"/>
                                                    <div className="text-white text-base font-semibold leading-normal">
                                                        Generate Assessments
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                            : (
                                <React.Fragment key={`content-assessments`}>
                                    <div id="assessments-automated" className="flex flex-col w-full justify-center items-center">
                                        {
                                            !isGenerator && (
                                                <div className="flex flex-col gap-3 mb-4">
                                                    <div className="inline-flex justify-center items-center gap-3 p-2 content-center border-1 bg-yellow-50 border-orange-500 rounded-md">
                                                        <img className="w-[18px] h-[18px] relative bg-gray-50 rounded-full" src="/icons/infoOrange.svg" alt="icon"/>
                                                        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex text-wrap">
                                                            <div className="self-stretch text-wrap text-gray-600  text-base max-[1200px]:text-sm font-normal leading-normal">
                                                                <span
                                                                    dangerouslySetInnerHTML={
                                                                        {
                                                                            __html: `These Assessments were automatically generated during report creation to include<br/>`
                                                                                + `statements for improving or declining Outcome Measures. Before approving the report,<br/>`
                                                                                + `ensure you are <b>satisfied with the selected Outcome Measures</b> in the Objective<br/>`
                                                                                + `Outcomes section and be sure to <b>proofread the Assessments for accuracy</b>.`
                                                                        }
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-center">
                                                        <button
                                                            onClick={() => setShowAssessmentDeterminationsModal(true)}
                                                            disabled={assessmentDecisions.length === 0}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                                                        >
                                                            View Assessment Determinations
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        {
                                            isGenerator && assessmentsGenerated && assessmentDecisions.length > 0 && (
                                                <div className="flex flex-col gap-3 mb-4">
                                                    <div className="inline-flex justify-center items-center gap-3 p-2 content-center border-1 bg-yellow-50 border-orange-500 rounded-md">
                                                        <img className="w-[18px] h-[18px] relative bg-gray-50 rounded-full" src="/icons/infoOrange.svg" alt="icon"/>
                                                        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex text-wrap">
                                                            <div className="self-stretch text-wrap text-gray-600  text-base max-[1200px]:text-sm font-normal leading-normal">
                                                                <span
                                                                    dangerouslySetInnerHTML={
                                                                        {
                                                                            __html: `These Assessments were automatically generated to include statements for improving<br/>`
                                                                                + `or declining Outcome Measures. Before submitting the report, ensure you are<br/>`
                                                                                + `<b>satisfied with the selected Outcome Measures</b> in the Objective Outcomes section<br/>`
                                                                                + `and be sure to <b>proofread the Assessments for accuracy</b>.`
                                                                        }
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-center">
                                                        <button
                                                            onClick={() => setShowAssessmentDeterminationsModal(true)}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                                                        >
                                                            View Assessment Determinations
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        <div id="assessments" className="flex flex-col w-full justify-center items-center gap-4 p-5">
                                            {/* --- Physical Assessments --- */}
                                            <div id="physical-assessments" className="w-[80%]">
                                                <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0">
                                                    <label className={"justify-start items-center flex text-gray-900 gap-x-2 text-lg font-semibold leading-7"}>
                                                        <ManagedInput
                                                            id={"include-physical-assessments"}
                                                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                            type="checkbox"
                                                            value={includePhysicalAssessments}
                                                            updateValue={checked => setIncludePhysicalAssessments(checked)}
                                                        />
                                                        Physical Assessments
                                                    </label>
                                                </div>
                                                <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                                        Write an objective summary of improvements in physical function. Refer to Outcome Measures and/or patient reported milestones (ROM, Activity, Disability, Function, Sleep, Pain, Work, Medication.)
                                                    </div>
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                                        <div className="w-full gap-2">
                                                            <ManagedTextArea
                                                                id="physical-assessments-input"
                                                                className={`w-full resize-none p-2 rounded-lg shadow border border-gray-300 justify-start items-start inline-flex ${includePhysicalAssessments ? "text-gray-600" : "text-gray-400"}`}
                                                                disabled={!includePhysicalAssessments}
                                                                placeholder="Enter Assessments"
                                                                rows={10}
                                                                // useDirtyState props
                                                                value={physicalAssessments}
                                                                updateValue={updatePhysicalAssessments}
                                                            />
                                                            <span className="text-right w-full">
                                                                <span className={`text-sm float-right ${!includePhysicalAssessments ? "text-gray-400" : physicalAssessments?.length <= 2000 ? "text-gray-600" : "text-error-600"}`}>
                                                                    {`${physicalAssessments ? physicalAssessments.length : 0}/2000`}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                                            </div>
                                            {/* --- Behavioral Assessments --- */}
                                            <div id="behavioral-assessments" className="w-[80%]">
                                                <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0">
                                                    <label className={"justify-start items-center flex text-gray-900 gap-x-2 text-lg font-semibold leading-7"}>
                                                        <ManagedInput
                                                            id={"include-behavioral-assessments"}
                                                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                            type="checkbox"
                                                            value={includeBehavioralAssessments}
                                                            updateValue={checked => setIncludeBehavioralAssessments(checked)}
                                                        />
                                                        Behavioral Assessments
                                                    </label>
                                                </div>
                                                <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                                        Write an objective summary of improvements in behavioral and cognitive function. Refer to behavioral Outcome Measures and/or patient-reported milestones (Mood, Confidence, PSEQ, PCS, FABQ, Positive Behaviors, Stress Reduction, Engagement/Education.)
                                                    </div>
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                                        <div className="w-full gap-2">
                                                            <ManagedTextArea
                                                                id="behavioral-assessments-input"
                                                                className={`w-full resize-none p-2 rounded-lg shadow border border-gray-300 justify-start items-start inline-flex ${includeBehavioralAssessments ? "text-gray-600" : "text-gray-400"}`}
                                                                disabled={!includeBehavioralAssessments}
                                                                placeholder="Enter Assessments"
                                                                rows={10}
                                                                // useDirtyState props
                                                                value={behavioralAssessments}
                                                                updateValue={updateBehavioralAssessments}
                                                            />
                                                            <span className="text-right w-full">
                                                                <span className={`text-sm float-right ${!includeBehavioralAssessments ? "text-gray-400" : behavioralAssessments?.length <= 2000 ? "text-gray-600" : "text-error-600"}`}>
                                                                    {`${behavioralAssessments ? behavioralAssessments.length : 0}/2000`}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                                            </div>
                                            {/* --- Social Assessments --- */}
                                            <div id="social-assessments" className="w-[80%]">
                                                <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0">
                                                    <label className={"justify-start items-center flex text-gray-900 gap-x-2 text-lg font-semibold leading-7"}>
                                                        <ManagedInput
                                                            id={"include-social-assessments"}
                                                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                            type="checkbox"
                                                            value={includeSocialAssessments}
                                                            updateValue={checked => setIncludeSocialAssessments(checked)}
                                                        />
                                                        Social Assessments
                                                    </label>
                                                </div>
                                                <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                                        Write an objective summary of improvements in social life. Refer to objective evidence reported to you by the patient (Connection, Communication, Support, Relationships, and Community.)
                                                    </div>
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                                        <div className="w-full gap-2">
                                                            <ManagedTextArea
                                                                id="social-assessments-input"
                                                                className={`w-full resize-none p-2 rounded-lg shadow border border-gray-300 justify-start items-start inline-flex ${includeSocialAssessments ? "text-gray-600" : "text-gray-400"}`}
                                                                disabled={!includeSocialAssessments}
                                                                placeholder="Enter Assessments"
                                                                rows={10}
                                                                // useDirtyState props
                                                                value={socialAssessments}
                                                                updateValue={updateSocialAssessments}
                                                            />
                                                            <span className="self-stretch flex justify-between items-start">
                                                                {
                                                                    !isGenerator && (
                                                                        <span className={`flex items-center gap-1 ${includeSocialAssessments ? "text-gray-600" : "text-gray-400"}`}>
                                                                            <button
                                                                                className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border cursor-pointer"
                                                                                onClick={event => {event.preventDefault(); dispatchRevertSocialAssessments()}}
                                                                            >
                                                                                <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                                            </button>
                                                                            <span className="whitespace-nowrap">
                                                                                Restore Unedited Social Assessments
                                                                            </span>
                                                                        </span>
                                                                    )
                                                                }
                                                                <span className="text-right w-full">
                                                                    <span className={`text-sm ${!includeSocialAssessments ? "text-gray-400" : socialAssessments?.length <= 2000 ? "text-gray-600" : "text-error-600"}`}>
                                                                        {`${socialAssessments ? socialAssessments.length : 0}/2000`}
                                                                    </span>
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                                            </div>
                                            {/* --- Continued Needs Assessments --- */}
                                            <div id="continued-needs-assessments" className="w-[80%]">
                                                <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0">
                                                    <label className={"justify-start items-center flex text-gray-900 gap-x-2 text-lg font-semibold leading-7"}>
                                                        <ManagedInput
                                                            id={"include-continued-needs-assessments"}
                                                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                            type="checkbox"
                                                            value={includeContinuedNeedsAssessments}
                                                            updateValue={checked => setIncludeContinuedNeedsAssessments(checked)}
                                                        />
                                                        Continued Needs
                                                    </label>
                                                </div>
                                                <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                                        Write a summary of any aspects of the patient's life which require ongoing focus and attention. Refer to objective evidence such as declining or stagnating Outcome Measures.
                                                    </div>
                                                    <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                                        <div className="w-full gap-2">
                                                            <ManagedTextArea
                                                                id="continued-needs-assessments-input"
                                                                className={`w-full resize-none p-2 rounded-lg shadow border border-gray-300 justify-start items-start inline-flex ${includeContinuedNeedsAssessments ? "text-gray-600" : "text-gray-400"}`}
                                                                disabled={!includeContinuedNeedsAssessments}
                                                                placeholder="Enter Assessments"
                                                                rows={10}
                                                                // useDirtyState props
                                                                value={continuedNeedsAssessments}
                                                                updateValue={updateContinuedNeedsAssessments}
                                                            />
                                                            <span className="text-right w-full">
                                                                <span className={`text-sm float-right ${!includeContinuedNeedsAssessments ? "text-gray-400" : continuedNeedsAssessments?.length <= 2500 ? "text-gray-600" : "text-error-600"}`}>
                                                                    {`${continuedNeedsAssessments ? continuedNeedsAssessments.length : 0}/2500`}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                    }
                </ReportSectionDivider>
                <ReportSectionDivider key={`divider-plan`} label={"Plan"}>
                    <div id="plan" className="flex flex-col w-full justify-center items-center gap-4 p-5">
                        {/* --- Plan Templates --- */}
                        {
                            <div id="plan-templates" className="w-[80%]">
                                <div
                                    id="plan-recommendations-grid"
                                    className="grid grid-cols-[min-content_min-content_minmax(0,1fr)] gap-x-0 gap-y-0 bg-gray-25"
                                >
                                    <div className="col-span-4 pl-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-t border-l border-r shadow-sm border-gray-300 rounded-t-md mt-4 first:mt-0">
                                        Templates
                                    </div>
                                    <div className="col-span-4 px-7 py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight border-gray-300 border-l border-r">
                                        Choose any pre-written templates first, then edit the final plan below. Modifying the templates will overwrite any custom changes made to the final plan.
                                    </div>
                                    <div className="col-span-3 h-3 border-l border-r border-gray-300"></div>
                                    <div id="continue-program" className="contents">
                                        <div className="inline-flex pl-7 py-3 justify-start whitespace-nowrap border-l border-gray-300 col-start-1">
                                            <label className="justify-start items-center flex text-gray-600 gap-x-2 text-base font-normal leading-normal">
                                                <ManagedInput
                                                    className="w-4 h-4 p-0.5 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                    id={"continue-program"}
                                                    type="checkbox"
                                                    value={planSettings.recommendProgramContinuation}
                                                    updateValue={checked => dispatchUpdateRecommendProgramContinuation(checked)}
                                                />
                                                Continue Program
                                            </label>
                                        </div>
                                        <div className={`col-start-2`}></div>
                                        <div className={`ml-[-1px] border-r border-gray-300 col-start-3`}></div>
                                    </div>
                                    <div id="completed-program" className="contents">
                                        <div className="inline-flex pl-7 py-3 justify-start whitespace-nowrap border-l border-gray-300 col-start-1">
                                            <label className="justify-start items-center flex text-gray-600 gap-x-2 text-base font-normal leading-normal">
                                                <ManagedInput
                                                    className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                    id={"completed-program"}
                                                    type="checkbox"
                                                    value={planSettings.recommendProgramEnd}
                                                    updateValue={checked => dispatchUpdateRecommendProgramEnd(checked)}
                                                />
                                                Completed 12-Week Program
                                            </label>
                                        </div>
                                        <div className={`col-start-2`}></div>
                                        <div className={`ml-[-1px] border-r border-gray-300 col-start-3`}></div>
                                    </div>
                                    <div id="physician-notification" className="contents">
                                        <div className="inline-flex pl-7 py-3 justify-start whitespace-nowrap border-l border-gray-300 col-start-1">
                                            <label className="justify-start items-center flex text-gray-600 gap-x-2 text-base font-normal leading-normal">
                                                <ManagedInput
                                                    className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                    id={"physician-notification"}
                                                    type="checkbox"
                                                    value={planSettings.physicianNotification}
                                                    updateValue={checked => dispatchUpdatePhysicianNotification(checked)}
                                                />
                                                Physician Notification
                                            </label>
                                        </div>
                                        <div className="px-4 py-0 text-center font-normal text-gray-600 leading-normal col-start-2">
                                            <CustomDropDown
                                                defaultValue={planSettings.physicianNotificationType}
                                                locked={!planSettings.physicianNotification}
                                                options={RD.physicianNotificationsOptions}
                                                optionsAlignRight={false}
                                                // Callbacks
                                                onSelectionCallback={dispatchUpdatePhysicianNotificationType}
                                            />
                                        </div>
                                        <div className={`ml-[-1px] border-r border-gray-300 col-start-3`}></div>
                                    </div>
                                    <div id="program-extension" className="contents">
                                        <div className="inline-flex pl-7 py-3 justify-start whitespace-nowrap border-l border-gray-300 col-start-1">
                                            <label className="justify-start items-center flex text-gray-600 gap-x-2 text-base font-normal leading-normal">
                                                <ManagedInput
                                                    className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer"
                                                    id={"program-extension"}
                                                    type="checkbox"
                                                    value={planSettings.recommendProgramExtension}
                                                    updateValue={checked => dispatchUpdateRecommendProgramExtension(checked)}
                                                />
                                                Recommend Program Extension
                                            </label>
                                        </div>
                                        <div className="px-4 py-0 text-center font-normal text-gray-600 leading-normal col-start-2">
                                            <CustomDropDown
                                                defaultValue={planSettings.extensionWeeks}
                                                locked={!planSettings.recommendProgramExtension}
                                                options={RD.extensionWeekOptions}
                                                optionsAlignRight={false}
                                                // Callbacks
                                                onSelectionCallback={dispatchUpdateExtensionWeeks}
                                            />
                                        </div>
                                        <div className={`ml-[-1px] border-r border-gray-300 col-start-3`}></div>
                                    </div>
                                    <div className="col-span-3 h-3 border-b border-l border-r shadow-sm border-gray-300 rounded-b-md"></div>
                                </div>
                            </div>
                        }
                        {/* --- Plan --- */}
                        <div className="w-[80%]">
                            <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r  rounded-t-md shadow-sm mt-4 first:mt-0">
                                Final Plan
                            </div>
                            <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm">
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                                    The Plan section should provide a concise overview of the recommended next steps in the patient's care. Keep in mind that physicians and payers often refer to this section first for a quick understanding of the care direction.
                                </div>
                                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                                    <div className="w-full gap-2">
                                        <ManagedTextArea
                                            id="plan-input"
                                            className="w-full resize-none h-[15vh] px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-start gap-2 inline-flex"
                                            placeholder="Enter Plan"
                                            rows={10}
                                            // useDirtyState props
                                            value={plan}
                                            updateValue={updatePlan}
                                        />
                                    </div>
                                    {
                                        !isGenerator && (
                                            <span className="text-gray-600 flex items-center gap-1">
                                                <button
                                                    className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border cursor-pointer"
                                                    onClick={e => {e.preventDefault(); dispatchRevertPlan();}}
                                                >
                                                    <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                </button>
                                                <span className="whitespace-nowrap">
                                                    Restore Unedited Plan
                                                </span>
                                            </span>
                                        )
                                    }
                                </div>
                            </div>
                            <div className="px-7 pb-4 bg-gray-25 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
                        </div>
                    </div>
                </ReportSectionDivider>
                {
                    RDP.ENABLE_CITATIONS_DISPLAY && citations && citations.length > 0 && (
                        <ReportSectionDivider
                            key={`divider-citations`}
                            label={"Citations"}
                            labelClassName="text-lg text-gray-400"
                            chevronClassName="text-gray-400"
                        >
                            <div id="citations" className="flex flex-col w-full justify-center items-center gap-4 p-5">
                                <div className="w-[80%]">
                                    {
                                        (() => {
                                            // Group citations by week/month/session
                                            const grouped: Record<string, { type: 'week' | 'month' | 'session', number: number, citations: string[] }> = {};

                                            citations.forEach((citation) => {
                                                const weekMatch = citation.name.match(/^\[W(\d+)\]/);
                                                const monthMatch = citation.name.match(/^\[M(\d+)\]/);
                                                const sessionMatch = citation.name.match(/^\[S(\d+)\]/);

                                                if (weekMatch) {
                                                    const weekNum = parseInt(weekMatch[1]);
                                                    const key = `W${weekNum}`;
                                                    if (!grouped[key]) {
                                                        grouped[key] = { type: 'week', number: weekNum, citations: [] };
                                                    }
                                                    grouped[key].citations.push(citation.name);
                                                } else if (monthMatch) {
                                                    const monthNum = parseInt(monthMatch[1]);
                                                    const key = `M${monthNum}`;
                                                    if (!grouped[key]) {
                                                        grouped[key] = { type: 'month', number: monthNum, citations: [] };
                                                    }
                                                    grouped[key].citations.push(citation.name);
                                                } else if (sessionMatch) {
                                                    const sessionNum = parseInt(sessionMatch[1]);
                                                    const key = `S${sessionNum}`;
                                                    if (!grouped[key]) {
                                                        grouped[key] = { type: 'session', number: sessionNum, citations: [] };
                                                    }
                                                    grouped[key].citations.push(citation.name);
                                                }
                                            });

                                            // Sort: weeks first, then sessions, then months (each by number)
                                            const sortedEntries = Object.entries(grouped).sort((a, b) => {
                                                const [, groupA] = a;
                                                const [, groupB] = b;

                                                // Define type priority: week = 1, session = 2, month = 3
                                                const typePriority = { week: 1, session: 2, month: 3 };
                                                const priorityA = typePriority[groupA.type];
                                                const priorityB = typePriority[groupB.type];

                                                if (priorityA !== priorityB) return priorityA - priorityB;

                                                return groupA.number - groupB.number;
                                            });

                                            return (
                                                <div className="flex justify-center">
                                                    <table className="border-collapse">
                                                        <tbody>
                                                            {sortedEntries.map(([key, group], idx) => (
                                                                <tr key={key}>
                                                                    <td className={`py-2 px-3 text-gray-400 text-sm align-top whitespace-nowrap border-b border-gray-200 ${idx === 0 ? 'border-t' : ''}`}>
                                                                        {group.type === 'week' ? `Week ${group.number}` : group.type === 'session' ? `Session ${group.number}` : `Month ${group.number}`}
                                                                    </td>
                                                                    <td className={`py-2 px-3 border-l border-b border-gray-200 ${idx === 0 ? 'border-t' : ''}`}>
                                                                        <div className="space-y-1">
                                                                            {group.citations.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((citation, idx) => (
                                                                                <div key={idx} className="text-gray-400 text-sm whitespace-nowrap">
                                                                                    {citation}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })()
                                    }
                                </div>
                            </div>
                        </ReportSectionDivider>
                    )
                }
                {
                    RDP.ENABLE_CITATIONS_DISPLAY && (!citations || citations.length === 0) && (
                        <div className="w-full flex-col">
                            <div className="inline-flex relative mb-4 mt-2 items-center justify-center w-full py-2">
                                <hr className="w-full h-0.5 bg-gray-200 border-0 rounded" />
                                <div className="inline-flex absolute px-2 -translate-x-1/2 bg-white left-1/2 text-center text-lg text-gray-400 leading-tight">
                                    Report not generated with Citations
                                </div>
                            </div>
                        </div>
                    )
                }
                <ReportSectionDivider key={`divider-report-action-buttons`} locked={true} label={""}>
                    <div id="report-action-buttons" className="flex flex-col w-full justify-center items-center">
                        {
                            isGenerator
                                ? (
                                    <>
                                        <div className="flex flex-col justify-start items-start h-11 gap-4 mb-4">
                                            <button
                                                className={`self-stretch px-8 py-2.5 bg-brand-600 hover:bg-brand-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex ${notificationManager.hasErrors() ? "opacity-50" : ""} cursor-pointer disabled:cursor-not-allowed`}
                                                onClick={
                                                    e => {
                                                        e.preventDefault();
                                                        if (!notificationManager.hasErrors()) {
                                                            setTriggerSubmitForReview(prev => ++prev);
                                                        }
                                                    }
                                                }
                                                disabled={notificationManager.hasErrors()}
                                            >
                                                <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                    <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/file-check-02.svg" alt="icon"/>
                                                    <div className="text-white text-base font-semibold leading-normal">
                                                        Submit For Review
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                        <div className="flex flex-col justify-start items-start h-11 gap-4 mb-4">
                                            <button
                                                className="self-stretch px-8 py-2.5 bg-error-600 hover:bg-error-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex cursor-pointer"
                                                onClick={e => {e.preventDefault(); setTriggerDeleteReportWarning(prev => ++prev);}}
                                            >
                                                <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                    <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/delete.svg" alt="icon"/>
                                                    <div className="text-white text-base font-semibold leading-normal">
                                                        Discard Without Submitting
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </>
                                )
                                : (
                                    <>
                                        <div className="flex flex-col justify-start items-start h-11 gap-4 mb-2">
                                            <button
                                                className={`self-stretch px-8 py-2.5 bg-success-600 hover:bg-success-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex ${notificationManager.hasErrors() ? "opacity-50" : ""} cursor-pointer disabled:cursor-not-allowed`}
                                                onClick={
                                                    e => {
                                                        e.preventDefault();
                                                        if (!notificationManager.hasErrors()) {
                                                            setTriggerApproveReport(prev => ++prev);
                                                        }
                                                    }
                                                }
                                                disabled={notificationManager.hasErrors()}
                                            >
                                                <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                    <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/file-check-02.svg" alt="icon"/>
                                                    <div className="text-white text-base font-semibold leading-normal">
                                                        Approve Report
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                        <div className="flex flex-col justify-start items-start h-11 gap-4 mb-4">
                                            <button
                                                className="self-stretch px-8 py-2.5 bg-error-600 hover:bg-error-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex cursor-pointer"
                                                onClick={e => {e.preventDefault(); setTriggerDeleteReportWarning(prev => ++prev);}}
                                            >
                                                <div className="px-0.5 justify-center gap-x-1 items-center inline-flex">
                                                    <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/delete.svg" alt="icon"/>
                                                    <div className="text-white text-base font-semibold leading-normal">
                                                        Delete Report
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </>
                                )
                        }
                    </div>
                </ReportSectionDivider>
            </div>
        );
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={headerConfig ? headerConfig : getModularHeaderConfig()}
            internal={true}
            logoutHook={logoutHook}
        >
            {/* Render the AutoSaveReportPatcher component */}
            {
                isDirtyStateManagerInitialized && (
                    <AutoSaveReportPatcher
                        onAutoSaveDataGenerated={setAutoSaveGeneratedData}
                        onAutoSaveStatusChanged={setAutoSaveStatus}
                    />
                )
            }
            {/* Render page UI */}
            {
                isLoading
                    ? (
                        renderLoader(loadingMessage)
                    )
                    : (
                        !report
                            ? (
                                <div id="body" className={"w-full p-4 h-full gap-y-1 flex-col inline-flex justify-start items-center whitespace-nowrap overflow-x-hidden overflow-y-auto"}>
                                    <div className=" w-full h-full justify-center items-center inline-flex flex-col	">
                                        <p className="px-6 text-gray-900 text-lg font-medium ">
                                            The Outcome Report could not be loaded. Contact a Karuna Admin for support.
                                        </p>
                                    </div>
                                </div>
                            )
                            : (
                                renderReport()
                            )
                    )
            }

            {/* Assessment Determinations Modal */}
            {showAssessmentDeterminationsModal && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/25 px-8 py-16" onClick={() => setShowAssessmentDeterminationsModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                            <h2 className="text-xl font-bold text-gray-900">📋 Assessment Determinations</h2>
                            <button
                                onClick={() => setShowAssessmentDeterminationsModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            <AssessmentDeterminations decisions={assessmentDecisions} showTooltips={false} />
                        </div>
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                            <button
                                onClick={() => setShowAssessmentDeterminationsModal(false)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Page>
    );
};

export default ReportProcessor;